import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefusedRowTag, useRefusedRowIds } from '../components/RefusedChangeBanner'
import { loadDeletedLotLabels, withoutDeletedLotLogs } from '../utils/deletedLots'
import { describeMortalityCauses } from '../utils/mortalityByCause'
import { useLocation, useNavigate } from 'react-router-dom'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import { AverageWeightGrowthChart } from '../components/AverageWeightGrowthChart'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import FormField from '../components/FormField'
import GradientButton from '../components/GradientButton'
import type { MatrixData } from '../components/MatrixTable'
import type { FeedLog, FishStockLog, HarvestLog, MortalityLog, WeightSampleLog } from '../types/production'
import type { LotLineageProjectionRow } from '../types/lot'
import type { EntityBatchSyncStatus, EntityMutationResult, EntityMutationSyncState } from '../utils/entityMutationSync'
import { ensureFarmList } from '../utils/farmWizard'
import { loadMortalityMatrix } from '../utils/mortalityMatrixApi'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { getDefaultMatrixForSpecies } from '../utils/mortalityMatrix'
import {
  buildLatestTemperatureByLotWeek,
  deriveObservedMortalityMetrics,
  normalizeProjectionWaterLogs,
  resolveMortalityProjectionForRow,
  type ProjectionWaterRow
} from '../utils/mortalityProjection'
import { formatStoredDateTimeAsLocal, normalizeToIsoZMidnight, toLocalCalendarDayKey } from '../utils/dateNormalization'
import { readScopedString, writeScopedString } from '../utils/scopedStorage'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { partitionRowsByLotRoster } from '../utils/biomassLotScoping'
import { createId } from '../utils/id'
import { parseHumanNumber } from '../utils/numberInput'
import { filterInteractiveLots, isLotActiveForInteraction, toProductionRowStage } from '../utils/lotInteraction'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { loadStoredSpecies, type ProductionSpeciesKey } from '../data/productionSpeciesPresets'
import { buildBiomassAsOfResolver } from '../utils/biomassAsOfResolver'
import {
  deriveMortalityPerformanceSnapshot,
  type MortalityPerformanceSnapshot
} from '../utils/biomassPerformanceSnapshot'
import { deriveCurrentTrendProjectionScenario } from '../utils/biomassProjectionScenarios'
import { deriveBiomassProjectionScenarioSummary } from '../utils/biomassProjectionScenarioSummary'
import { resolveBiomassKpiLotIdentity } from './biomass/kpiLotIdentity'
import { resolveBiomassDraftLot, resolveBiomassDraftLotId } from './biomass/lotResolution'
import {
  buildImportWorkspaceReviewNotice,
  parseImportWorkspaceReviewRouteState
} from './importWorkspace/importWorkspaceSession'
import {
  buildBiomassSamplingMortalityMetadata,
  findAllLinkedMortalityLogsForRow,
  overlayMortalityLogsOnProductionRows
} from './biomass/mortalityLogBridge'
import {
  buildBiomassSamplingWeightMetadata,
  computeWeightSampleRollupByRowId,
  overlayWeightSamplesOnProductionRows
} from './biomass/weightSampleLogBridge'
import {
  buildBiomassFishStockMetadata,
  findLinkedFishStockLog,
  overlayFishStockOnProductionRows
} from './biomass/fishStockLogBridge'
import { CrewCorrectionButton } from './crewCorrection/CrewCorrectionButton'
import { CrewRowBadges, useCorrectionCounts } from './crewCorrection/CrewRowBadges'
import {
  buildBiomassHarvestMetadata,
  overlayHarvestOnProductionRows
} from './biomass/harvestLogBridge'
import {
  countPlannedLinkedEventDeletes,
  executeProductionRecordLinkedEventDeletes,
  planProductionRecordLinkedEventDeletes
} from './biomass/productionRecordCascadeDelete'
import { overlayLotStageOnProductionRows } from './biomass/lotStageBridge'
import { weightSampleLogApi } from '../utils/weightSampleLogApi'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { harvestLogApi } from '../utils/harvestLogApi'
import { feedLogApi } from '../utils/feedLogApi'
import { computeFcrInFlight } from '../utils/feedConversionRatio'
import { buildLotFcrInputs } from '../utils/lotFeedConversion'
import { lotApi } from '../utils/lotApi'
import { computeCurrentBiomassPerLot } from '../utils/currentBiomassPerLot'
import { applyCurrentLotState } from './biomass/currentLotCards'
import { evaluateHarvestTransitionSignal } from './biomass/harvestTransitionThresholds'
import { MortalityEventsPopover } from './biomass/MortalityEventsPopover'
import { SamplingEventsPopover } from './biomass/SamplingEventsPopover'
import { isAnyBiomassDraftDirty } from './biomass/biomassDraftDirty'
import { validateSamplingDraft } from './biomass/samplingDraftValidation'
import { getBiomassRecentRowTitle } from './biomass/lotDisplayLabel'
import { belongsInSamplingList, hasStockSignal, isCountRow } from './biomass/recentRowsClassification'
import {
  listProductionUnitTableRows,
  refreshProductionUnitTableFromApi,
  readProductionUnitTableRowSyncStatus,
  retryProductionUnitTableRowSync,
  retryProductionUnitTableRowSyncStatus,
  upsertProductionUnitTableRowAndSync,
  deleteProductionUnitTableRowAndSync,
  type BiomassDataRow,
  type ProductionUnitTableContext
} from '../utils/productionUnitTable'
import { deriveBiomassSyncOutcome, type BiomassSyncOutcome } from '../utils/biomassSyncOutcome'
import { reconcileSyncMutationState } from '../utils/syncMutationReconciler'
import { computeSyncBannerVisibility } from '../utils/syncBannerVisibility'
import { selectBiomassReconnectDrainTargets } from '../utils/biomassReconnectRetry'
import { computeDisplayedEndOfDayStockByRowId, type DisplayedEndOfDayStock } from '../utils/productionEndOfDayStock'
import { loadWaterTableRows, refreshWaterTableRowsFromApi, type WaterDataRow } from '../utils/waterTable'
import { useLots } from '../hooks/useLots'
import { useCrossFarmLotHistorySupplement } from '../hooks/useCrossFarmLotHistorySupplement'
import { useLotLineageProjection } from '../hooks/useLotLineageProjection'
import { resolveCanonicalLotKey } from '../utils/lotIdentity'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import { DeleteProductionRowButton } from './biomass/DeleteProductionRowButton'
import { useT, type TranslationKey } from '../i18n'
import { buildUnitSelectOptions, isUnitIdInactive } from '../utils/unitStatus'
import { farmTimeZone } from '../utils/farmTime'
import { stockingDensityKgM3, unitVolumeM3 } from '../utils/density'
import { CAPACITY_STATUS_COLOR, capacityDensityLimit, capacityStatusWordKey, resolveLotCapacity, statusAgainstMaximum } from '../utils/carryingCapacity'
import { dropEmptySameDayDuplicateRows } from '../utils/emptyDayRows'
import { useDeletedLotRowMatcher } from '../hooks/useDeletedLotRowMatcher'

type NormalizedFieldResult =
  | { ok: true; normalized: string }
  | { ok: false; error: string }

type BiomassLogDraft = {
  id: string
  recordedAt: string
  lotId: string
  lotKey: string
  stage: 'Active' | 'Inactive'
  averageWeightG: string
  dailyMortality: string
  notes: string
}

const normalizeWholeNumberField = (
  rawValue: string,
  label: string,
  t: (key: any, vars?: Record<string, string | number>) => string
): NormalizedFieldResult => {
  const trimmed = String(rawValue ?? '').trim()
  if (trimmed === '') return { ok: true, normalized: '' }

  const parsed = parseHumanNumber(trimmed, { requireInteger: true })
  if (!parsed.ok) {
    return { ok: false, error: t('datatable.numericValidation.mustBeNumber', { label }) }
  }

  const numeric = parsed.value
  if (!Number.isInteger(numeric)) {
    return { ok: false, error: t('datatable.numericValidation.mustBeWholeNumber', { label }) }
  }

  return { ok: true, normalized: Math.round(numeric).toString() }
}

const mergeBatchSyncStatuses = (
  statuses: Array<EntityBatchSyncStatus | null | undefined>
): EntityBatchSyncStatus | null => {
  const stableStatuses = statuses.filter((status): status is EntityBatchSyncStatus => Boolean(status))
  if (stableStatuses.length === 0) return null

  const blockers = stableStatuses.flatMap((status) => status.blockers)
  const errorMessages = Array.from(new Set(stableStatuses.map((status) => String(status.errorMessage ?? '').trim()).filter(Boolean)))
  let syncState: EntityBatchSyncStatus['syncState'] = 'pending'
  if (stableStatuses.every((status) => status.syncState === 'synced')) {
    syncState = 'synced'
  } else if (stableStatuses.some((status) => status.syncState === 'offline')) {
    syncState = 'offline'
  }

  return {
    syncState,
    blockedCount: stableStatuses.reduce((total, status) => total + status.blockedCount, 0),
    errorMessage: errorMessages.length === 0 ? null : errorMessages.join('; '),
    blockers
  }
}

const buildProductionRowFromBiomassLogDraft = (draft: BiomassLogDraft): BiomassDataRow => {
  // T3-A-07b (2026-07-06): ProductionRecord biology cache retired.
  // Biology cells are seeded empty; overlays populate at render time from
  // the WSL / MortalityLog / FishStockLog events written alongside the row.
  return {
    id: draft.id,
    date: draft.recordedAt,
    lotId: draft.lotId,
    lot: draft.lotKey,
    stage: draft.stage,
    weight: '',
    fishStock: '',
    dailyMortality: '',
    upVol: draft.notes,
    health: '',
    healthDetail: ''
  }
}

const resolveObservedMortalitySource = (
  hasObservedMortalityInput: boolean,
  mortalityLogId: string
): 'mortalityLog' | 'productionSummary' | null => {
  if (!hasObservedMortalityInput) return null
  return mortalityLogId ? 'mortalityLog' : 'productionSummary'
}

const resolveCombinedEntitySyncState = (
  states: Array<EntityMutationSyncState | null | undefined>
): EntityMutationSyncState => {
  if (states.includes('offline')) return 'offline'
  if (states.includes('pending')) return 'pending'
  return 'synced'
}

const combineEntityMutationResults = (
  results: Array<Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'> | null>
): Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'> => {
  const activeResults = results.filter((result): result is Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'> => Boolean(result))
  const syncState = resolveCombinedEntitySyncState(activeResults.map((result) => result.syncState))

  if (!activeResults.some((result) => Boolean(result.syncPromise))) {
    return {
      syncState,
      syncPromise: null
    }
  }

  return {
    syncState,
    syncPromise: Promise.all(
      activeResults.map((result) => result.syncPromise ?? Promise.resolve(result.syncState))
    ).then((states) => resolveCombinedEntitySyncState(states))
  }
}
export type BiomassKPI = {
  lot: string
  lotToken: string
  stage: 'Active' | 'Inactive'
  date: string
  totalBiomassKg: number
  fishStock: number
  /** The row's own literally-entered stock, or `null` when it was blank on this row (derived-only). Distinct from `fishStock`, which may be mortality-netted for a receiving event (#32/#34). */
  rawStoredFishStock: number | null
  averageWeightG: number
  mortality: number
  hasObservedMortalityInput: boolean
  observedMortalitySource: 'mortalityLog' | 'productionSummary' | null
  observedMortalityRate: number | null
  mortalityRate: number | null
  survivalRate: number | null
  expectedMortalityRate: number | null
  projectedSurvivalRate: number | null
  projectedStock: number | null
  projectedBiomassKg: number | null
  projectedStockCurrentTrend: number | null
  projectedBiomassCurrentTrendKg: number | null
  temperatureC: number | null
  /** kg/m³; null when the unit has no volume (shown as "--", findings #98). */
  stockingDensity: number | null
  /** The farm's density limit for this lot's weight band and the unit type (findings #96); null = none set. */
  densityLimitKgM3: number | null
  /** The Capacidad de carga table the limit comes from (findings #151); null = none. */
  densityTableName?: string | null
  /** Mid-cycle FCR = feed kg to date ÷ current biomass kg. Null when harvested (settled FCR is the truth), no feed yet, or current biomass can't be derived. Shares the formula with the Reports "FCR in flight" column (`src/utils/feedConversionRatio.ts`) — same number, second surface. */
  feedConversionRatioInFlight: string | null
  week: string
  weekDisplay: string
}

const getWeekNumber = (date: string): string => {
  const normalized = normalizeToIsoZMidnight(date)
  if (!normalized.ok) {
    console.warn('Invalid date:', date)
    return 'Invalid-Date'
  }

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return 'Invalid-Date'

  const tempDate = new Date(Date.UTC(year, month - 1, day))
  const dayNum = tempDate.getUTCDay() || 7
  tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1))
  const weekNum = Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)

  return `${tempDate.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

const getBiomassKpiDateTime = (date: string): number | null => {
  const normalized = normalizeToIsoZMidnight(date)
  if (!normalized.ok) return null

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  return Date.UTC(year, month - 1, day)
}

const sortBiomassKpisChronologically = (kpis: BiomassKPI[]): BiomassKPI[] => (
  [...kpis].sort((left, right) => {
    const leftTime = getBiomassKpiDateTime(left.date)
    const rightTime = getBiomassKpiDateTime(right.date)
    if (leftTime !== null && rightTime !== null && leftTime !== rightTime) {
      return leftTime - rightTime
    }
    if (left.date !== right.date) return left.date.localeCompare(right.date)
    return left.lot.localeCompare(right.lot)
  })
)

const getProjectionDaysToNextRecord = (currentDate: string, nextDate: string | null): number => {
  if (!nextDate) return 1

  const currentTime = getBiomassKpiDateTime(currentDate)
  const nextTime = getBiomassKpiDateTime(nextDate)
  if (currentTime === null || nextTime === null || nextTime <= currentTime) return 1

  return Math.max(1, Math.floor((nextTime - currentTime) / 86_400_000))
}

const formatWeekDisplay = (date: string, week: string): string => {
  if (week === 'Invalid-Date') return week

  const normalized = normalizeToIsoZMidnight(date)
  if (!normalized.ok) return week

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return week

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const monthName = monthNames[Math.max(0, Math.min(11, month - 1))] ?? 'Jan'
  return `${year} ${monthName} ${day} - ${week}`
}

const getWindowRef = (): Window | undefined => {
  const win = (globalThis as any).window as Window | undefined
  if (win === undefined) return undefined
  return win
}

const parseFloatOrZero = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value !== 'string') return 0
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatShrimpWeightG = (value: number): string => {
  if (!Number.isFinite(value)) return '0'
  if (value === 0) return '0'

  let minimumFractionDigits = 0
  let maximumFractionDigits = 1
  if (value > 0 && value < 0.01) {
    minimumFractionDigits = 4
    maximumFractionDigits = 4
  } else if (value < 1) {
    maximumFractionDigits = 3
  }

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value)
}

const asStableIdString = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

type ProductionUnitLike = {
  id?: unknown
  name?: unknown
  capacity?: unknown
  capacityUnit?: unknown
  type?: unknown
}

type FarmLike = {
  id?: unknown
  name?: unknown
  metadata?: Record<string, unknown> | null
  productionUnits?: ProductionUnitLike[]
}

const resolveSelectedFarm = (farms: FarmLike[], farmId: string): FarmLike | undefined => {
  return farms.find((farm) => asStableIdString(farm?.id) === farmId)
}

const resolveSelectedProductionUnit = (
  selectedFarm: FarmLike | undefined,
  productionUnitId: string
): ProductionUnitLike | undefined => {
  const productionUnits = selectedFarm?.productionUnits ?? []
  return productionUnits.find((unit) => asStableIdString(unit?.id) === productionUnitId)
}

const resolveUnitsAndProductionUnitSelection = (args: {
  farmId: string
  availableFarms: FarmLike[]
  productionUnitId: string
  preferredProductionUnitId?: string
}): { units: ProductionUnitLike[]; nextProductionUnitId: string } => {
  const { farmId, availableFarms, productionUnitId, preferredProductionUnitId } = args

  if (!farmId) return { units: [], nextProductionUnitId: '' }

  const selectedFarm = resolveSelectedFarm(availableFarms, farmId)
  if (!selectedFarm) return { units: [], nextProductionUnitId: '' }

  const units = selectedFarm.productionUnits ?? []
  if (units.length === 0) return { units, nextProductionUnitId: '' }

  const hasCurrent = Boolean(productionUnitId) && units.some((unit) => asStableIdString(unit?.id) === productionUnitId)
  if (hasCurrent) return { units, nextProductionUnitId: productionUnitId }

  const hasPreferred = Boolean(preferredProductionUnitId)
    && units.some((unit) => asStableIdString(unit?.id) === preferredProductionUnitId)

  if (hasPreferred) return { units, nextProductionUnitId: String(preferredProductionUnitId ?? '') }

  return { units, nextProductionUnitId: asStableIdString(units[0]?.id) }
}

const resolveFarmLabel = (selectedFarm: FarmLike | undefined, farmId: string): string => {
  const name = selectedFarm?.name
  if (typeof name === 'string' && name.trim().length > 0) return name
  if (farmId) return String(farmId)
  return '—'
}

const resolveProductionUnitLabel = (
  selectedProductionUnit: ProductionUnitLike | undefined,
  productionUnitId: string
): string => {
  const name = selectedProductionUnit?.name
  if (typeof name === 'string' && name.trim().length > 0) return name
  if (productionUnitId) return String(productionUnitId)
  return '—'
}

const formatStageLabelWithT = (
  t: (key: any, vars?: Record<string, string | number>) => string,
  stage: string
): string => {
  return stage === 'Inactive' ? t('lots.stage.inactive') : t('lots.stage.active')
}

const formatMethodLabelWithT = (
  t: (key: any, vars?: Record<string, string | number>) => string,
  method: string
): string => {
  if (method === 'exact') return t('biomass.method.exact')
  if (method === 'estimate') return t('biomass.method.estimate')
  return method
}

/**
 * Fase 7 (docs/tier-a-merge-consumer-mapping.md §2.6 line 364, consumer #16):
 * builds the token→lotId canonical map plus a parallel token→weight map from
 * the active lot roster and `LotLineageProjection` ancestor rows. Extracted
 * as a pure, exported function (mirrors `Feed.tsx`'s
 * `buildFeedCanonicalLotMaps`) so the weight-threading behavior is unit
 * testable directly, without needing to satisfy `filterRowsToLots` /
 * `resolveBiomassKpiLotIdentity`'s active-roster-only row-scoping rules to
 * exercise it end-to-end. Self entries are explicit weight `1` (own biomass
 * is never diluted); ancestor entries carry the fractional weight from
 * `LotLineageProjection` (first-weight-found per Option A). Descendants are
 * excluded — a descendant token belongs to its own identity going forward.
 */
export const buildBiomassCanonicalLotMaps = (args: {
  availableLots: Array<{ id: string; lotKey?: string | null }>
  lineageRows: readonly LotLineageProjectionRow[]
}): { canonicalLotIdByToken: Map<string, string>; canonicalWeightByToken: Map<string, number> } => {
  const canonicalLotIdByToken = new Map<string, string>()
  const canonicalWeightByToken = new Map<string, number>()
  args.availableLots.forEach((lot) => {
    const lotId = String(lot.id ?? '').trim()
    if (!lotId) return
    const token = resolveCanonicalLotKey(lot.lotKey)
    if (!token || token === 'unknown-lot') return
    canonicalLotIdByToken.set(token, lotId)
    canonicalWeightByToken.set(token, 1)
  })
  args.lineageRows.forEach((row) => {
    if (row.relation === 'descendant') return
    const lotId = row.lotId.trim()
    if (!lotId) return
    const token = resolveCanonicalLotKey(row.memberLotKey)
    if (!token || token === 'unknown-lot') return
    canonicalLotIdByToken.set(token, lotId)
    canonicalWeightByToken.set(token, row.weight)
  })
  return { canonicalLotIdByToken, canonicalWeightByToken }
}

export type BiomassBasicKpiPartitions = {
  active: BiomassKPI[]
  historical: BiomassKPI[]
}

export const buildBasicKpis = (args: {
  rows: BiomassDataRow[]
  availableLots: Array<{ id: string; lotKey?: string | null }>
  volumeInM3: number | null
  /** Density limit for an average weight in this unit and the lot's Capacidad de carga table (findings #96, #151). */
  densityLimitForWeight?: (averageWeightG: number, lotId?: string) => { limitKgM3: number | null; tableName: string | null }
  lineageRows?: readonly LotLineageProjectionRow[]
  /** Lifetime `FeedLog.feedKg` sum per lot, keyed by lotId. Used for the in-flight FCR ratio's numerator. */
  feedKgToDateByLotId?: ReadonlyMap<string, number>
  /** lotIds with at least one `HarvestLog` whose `harvestKg > 0` — in-flight FCR is suppressed (null) for these; the settled FCR is the truth once a lot has harvested. */
  harvestedLotIds?: ReadonlySet<string>
  /** v2.2 findings.md #215: stocked biomass per lotId (first count × first mean weight); the in-flight figure is eFCR. */
  stockedBiomassByLotId?: ReadonlyMap<string, number | null>
}): BiomassBasicKpiPartitions => {
  const { rows, availableLots, volumeInM3, densityLimitForWeight, lineageRows = [], feedKgToDateByLotId, harvestedLotIds, stockedBiomassByLotId } = args
  // U3 (docs/lot-lifecycle-vs-concerns-audit.md §2 M-Y12): `filterRowsToLots`
  // silently dropped rows for lots that moved out of this production unit or
  // were soft-deleted, zeroing out their biomass instead of surfacing it
  // under a Historico bucket. `partitionRowsByLotRoster`'s `active` output is
  // byte-identical to the old `filterRowsToLots` call; `historical` carries
  // the previously-dropped rows through the same per-row KPI builder below.
  const { active: activeRows, historical: historicalRows } = partitionRowsByLotRoster(rows, availableLots)

  // Fase 3 (docs/tier-a-merge-consumer-mapping.md §2.6): this map was
  // previously always empty (`new Map()`), which made the resolver's
  // token→lotId fallback an inert stub. Populate it from the current lot
  // roster plus `LotLineageProjection` ancestor rows so a legacy token
  // belonging to a lot absorbed by a cross-family merge still resolves to
  // the surviving lot's current biomass. Descendants are excluded — a
  // descendant token belongs to its own identity going forward.
  const { canonicalLotIdByToken, canonicalWeightByToken } = buildBiomassCanonicalLotMaps({
    availableLots,
    lineageRows
  })

  const buildKpisFromRows = (scopedDbData: BiomassDataRow[]): BiomassKPI[] => {
    // v2.1 finding #9 (companion to #6): when the Sampling form leaves
    // `fishStock` blank, derive the displayed end-of-day stock for each row from
    // the previous known stock minus daily mortality (after MORT-12 overlay).
    // Without this, the dashboard KPIs (Total Active Biomass, Total Fish stock,
    // Average Weight) silently report 0 when the active lot's only recent rows
    // carry no stored stock.
    const derivedStockByRowId = computeDisplayedEndOfDayStockByRowId(scopedDbData)
    const resolveBiomassAsOf = buildBiomassAsOfResolver({
      productionRows: scopedDbData,
      mortalityLogs: [],
      weightSampleLogs: [],
      fishStockLogs: [],
      canonicalLotIdByToken,
      canonicalWeightByToken
    })

    const basicKpis: BiomassKPI[] = []
    for (const row of scopedDbData) {
      if (!row?.date || !row?.weight) continue
      const storedStockTrimmed = typeof row.fishStock === 'string' ? row.fishStock.trim() : String(row.fishStock ?? '')
      const derived = derivedStockByRowId.get(String(row.id ?? ''))
      const stockSource = storedStockTrimmed ? storedStockTrimmed : (derived?.value ?? '')
      const { token: lotToken, label: lotLabel } = resolveBiomassKpiLotIdentity(row, availableLots)
      if (!lotToken) continue
      const lotId = typeof row.lotId === 'string' ? row.lotId.trim() : ''
      const helperEntry = resolveBiomassAsOf({
        lotId,
        lotToken,
        toRecordedAt: row.date
      })

      const weight = parseFloatOrZero(row.weight)
      const fallbackStock = stockSource ? parseFloatOrZero(stockSource) : 0
      const stock = helperEntry?.sourceStockCount ?? fallbackStock
      if (!(stock > 0)) continue

      const rowAny = row as any
      const rawMortality = typeof rowAny.dailyMortality === 'string' ? rowAny.dailyMortality : ''
      const hasObservedMortalityInput = rawMortality.trim() !== ''
      const mortality = hasObservedMortalityInput ? parseFloatOrZero(rawMortality) : 0
      const mortalityLogId = typeof rowAny.mortalityLogId === 'string' ? rowAny.mortalityLogId.trim() : ''
      const observedMortalitySource = resolveObservedMortalitySource(hasObservedMortalityInput, mortalityLogId)

      const totalBiomassKg = helperEntry?.biomassKg ?? ((stock * weight) / 1000)
      const stockingDensity = stockingDensityKgM3(totalBiomassKg, volumeInM3)
      const resolvedLotId = lotId || canonicalLotIdByToken.get(lotToken) || ''
      const densityLimit = densityLimitForWeight ? densityLimitForWeight(weight, resolvedLotId) : null
      const densityLimitKgM3 = densityLimit?.limitKgM3 ?? null
      const densityTableName = densityLimit?.tableName ?? null

      // v2.2 findings.md #215: eFCR en curso = feed to date ÷ (current −
      // stocked biomass). Unknown stocked biomass → no figure.
      const stockedBiomassKg = stockedBiomassByLotId?.get(resolvedLotId) ?? null
      const feedConversionRatioInFlight = stockedBiomassKg === null
        ? null
        : computeFcrInFlight(
          feedKgToDateByLotId?.get(resolvedLotId) ?? 0,
          totalBiomassKg - stockedBiomassKg,
          harvestedLotIds?.has(resolvedLotId) ?? false
        )

      const week = getWeekNumber(row.date)
      const weekDisplay = formatWeekDisplay(row.date, week)

      basicKpis.push({
        lot: lotLabel,
        lotToken,
        stage: row.stage,
        date: row.date,
        totalBiomassKg,
        fishStock: stock,
        // v2.2 #36: the row's own literally-entered stock, distinct from
        // `fishStock` above (which may be mortality-netted for a receiving
        // event, per #32/#34). `applySurvivalRates` needs the raw figure to
        // rate a lot's first stock event against its *received* count
        // (matching Database's dailyMortalityRate.ts and the ASC Appendix
        // 12.5.1 "stocking count" convention), not the already-netted one.
        rawStoredFishStock: storedStockTrimmed ? parseFloatOrZero(storedStockTrimmed) : null,
        averageWeightG: weight,
        mortality,
        hasObservedMortalityInput,
        observedMortalitySource,
        observedMortalityRate: null,
        mortalityRate: null,
        survivalRate: null,
        expectedMortalityRate: null,
        projectedSurvivalRate: null,
        projectedStock: null,
        projectedBiomassKg: null,
        projectedStockCurrentTrend: null,
        projectedBiomassCurrentTrendKg: null,
        temperatureC: null,
        stockingDensity,
        densityLimitKgM3,
        densityTableName,
        feedConversionRatioInFlight,
        week,
        weekDisplay
      })
    }

    return basicKpis
  }

  return {
    active: buildKpisFromRows(activeRows),
    historical: buildKpisFromRows(historicalRows)
  }
}

/**
 * U2 (docs/lot-lifecycle-vs-concerns-audit.md §2 M-Y9-agg): dedupe per lot
 * (most-recent snapshot wins — both `activeBasicKpis` and `historicalBasicKpis`
 * are expected sorted newest-first) and split into the "Biomasa activa total"
 * card's Activo / Historico buckets.
 *
 * Bucketing rule: an in-roster row (`activeBasicKpis`) only counts as Activo
 * when its live `stage` (post T3-D-04b overlay) is `'Active'` — an in-roster
 * lot the operator moved to Inactive/Harvested/Dormant still has current
 * production history and belongs in Historico, not silently dropped. An
 * out-of-roster row (`historicalBasicKpis`) always counts as Historico,
 * regardless of its stage snapshot, because for those rows the routing
 * signal is roster absence, not stage (T3-D-04b's overlay is a no-op for
 * lots outside the current roster).
 */
export const summarizeActiveAndHistoricalBiomassKpis = (
  activeBasicKpis: BiomassKPI[],
  historicalBasicKpis: BiomassKPI[]
): {
  activeLots: BiomassKPI[]
  historicalLots: BiomassKPI[]
  totalActiveBiomass: number
  totalHistoricalBiomass: number
} => {
  const activeLots: BiomassKPI[] = []
  const historicalLots: BiomassKPI[] = []
  const seenActiveLotTokens = new Set<string>()
  const seenHistoricalLotTokens = new Set<string>()

  for (const kpi of activeBasicKpis) {
    if (kpi.stage === 'Active') {
      if (seenActiveLotTokens.has(kpi.lotToken)) continue
      seenActiveLotTokens.add(kpi.lotToken)
      activeLots.push(kpi)
    } else {
      if (seenHistoricalLotTokens.has(kpi.lotToken)) continue
      seenHistoricalLotTokens.add(kpi.lotToken)
      historicalLots.push(kpi)
    }
  }

  for (const kpi of historicalBasicKpis) {
    if (seenHistoricalLotTokens.has(kpi.lotToken)) continue
    seenHistoricalLotTokens.add(kpi.lotToken)
    historicalLots.push(kpi)
  }

  let totalActiveBiomass = 0
  activeLots.forEach((kpi) => { totalActiveBiomass += kpi.totalBiomassKg })

  let totalHistoricalBiomass = 0
  historicalLots.forEach((kpi) => { totalHistoricalBiomass += kpi.totalBiomassKg })

  return { activeLots, historicalLots, totalActiveBiomass, totalHistoricalBiomass }
}

const groupKpisByLotToken = (kpis: BiomassKPI[]): Record<string, BiomassKPI[]> => {
  const groups: Record<string, BiomassKPI[]> = {}
  for (const kpi of kpis) {
    if (!groups[kpi.lotToken]) groups[kpi.lotToken] = []
    groups[kpi.lotToken].push(kpi)
  }
  return groups
}

export const applySurvivalRates = (groups: Record<string, BiomassKPI[]>): BiomassKPI[] => {
  const withRates: BiomassKPI[] = []
  for (const lotToken of Object.keys(groups)) {
    const lotKpisChronological = sortBiomassKpisChronologically(groups[lotToken] ?? [])
    for (let index = 0; index < lotKpisChronological.length; index += 1) {
      const kpi = lotKpisChronological[index]
      if (!kpi) continue

      // v2.2 #35/#36: a lot's first KPI row is its receiving/intake event —
      // rate its own same-day mortality against its own *received* stock
      // instead of leaving it null for lack of a previous row. Uses
      // `rawStoredFishStock` (the literal entered count), not `fishStock`
      // (which is mortality-netted for a receiving event per #32/#34) —
      // rating 600 dead against the already-netted 9400 double-counts the
      // loss and inflates the rate above what Database's
      // dailyMortalityRate.ts reports for the exact same event. Matches the
      // ASC Appendix 12.5.1 "stocking count" convention. Falls back to
      // `fishStock` in the unexpected case a first row has no raw entry.
      const previousKpi = index > 0 ? lotKpisChronological[index - 1] : undefined
      const baselineStock = previousKpi
        ? previousKpi.fishStock
        : (kpi.rawStoredFishStock ?? kpi.fishStock)

      const observedMortality = kpi.hasObservedMortalityInput
        ? deriveObservedMortalityMetrics({
          previousFishStock: baselineStock,
          dailyMortality: kpi.mortality
        })
        : null
      const derivedSurvivalRate = observedMortality
        ? Math.min(Math.max(observedMortality.observedSurvivalRate, 0), 100)
        : null

      withRates.push({
        ...kpi,
        observedMortalityRate: observedMortality?.observedMortalityRate ?? null,
        survivalRate: derivedSurvivalRate,
        mortalityRate: observedMortality?.observedMortalityRate ?? null
      })
    }
  }

  return withRates
}

const applyMortalityProjection = (args: {
  kpis: BiomassKPI[]
  waterRows: ProjectionWaterRow[]
  mortalityMatrix: MatrixData
  speciesKey: string
  availableLots: Array<{ id: string; lotKey?: string | null }>
}): BiomassKPI[] => {
  const { kpis, waterRows, mortalityMatrix, speciesKey, availableLots } = args
  const latestTemperatureByLotWeek = buildLatestTemperatureByLotWeek(waterRows, availableLots)

  const withProjection: BiomassKPI[] = []
  const groups = groupKpisByLotToken(kpis)

  for (const lotToken of Object.keys(groups)) {
    const lotKpisChronological = sortBiomassKpisChronologically(groups[lotToken] ?? [])

    for (let index = 0; index < lotKpisChronological.length; index += 1) {
      const kpi = lotKpisChronological[index]
      if (!kpi) continue

      const nextKpi = lotKpisChronological[index + 1] ?? null
      const daysToProject = getProjectionDaysToNextRecord(kpi.date, nextKpi?.date ?? null)
      const projection = resolveMortalityProjectionForRow({
        row: {
          date: kpi.date,
          lot: kpi.lot,
          weight: kpi.averageWeightG,
          fishStock: kpi.fishStock
        },
        availableLots,
        latestTemperatureByLotWeek,
        speciesKey,
        mortalityMatrix,
        daysToProject
      })

      const currentTrendScenario = deriveCurrentTrendProjectionScenario({
        initialStock: kpi.fishStock,
        averageWeightG: kpi.averageWeightG,
        dailyMortalityRatePct: kpi.observedMortalityRate,
        daysToProject
      })

      if (!projection) {
        withProjection.push({
          ...kpi,
          projectedStockCurrentTrend: currentTrendScenario.projectedStock,
          projectedBiomassCurrentTrendKg: currentTrendScenario.projectedBiomassKg
        })
        continue
      }

      withProjection.push({
        ...kpi,
        expectedMortalityRate: projection.expectedMortalityRate,
        projectedSurvivalRate: projection.projectedSurvivalRate,
        projectedStock: projection.projectedStock,
        projectedBiomassKg: projection.projectedBiomassKg,
        projectedStockCurrentTrend: currentTrendScenario.projectedStock,
        projectedBiomassCurrentTrendKg: currentTrendScenario.projectedBiomassKg,
        temperatureC: projection.temperatureC
      })
    }
  }

  return withProjection
}

type BiomassAnalyzeBodyProps = Readonly<{
  t: (key: any, vars?: Record<string, string | number>) => string
  biomassKPIs: BiomassKPI[]
  performanceSnapshot: MortalityPerformanceSnapshot | null
  viewMode: 'current' | 'historical' | 'charts'
  isSmallScreen: boolean
  stockLabel: string
  totalActiveBiomass: number
  totalActiveStock: number
  averageWeight: number
  averageObservedMortalityRate: number | null
  observedLotsCount: number
  averageSurvivalRate: number | null
  survivalLotsCount: number
  totalProjectedBiomass: number | null
  totalProjectedBiomassCurrentTrend: number | null
  averageExpectedMortalityRate: number | null
  projectedLotsCount: number
  projectedTrendLotsCount: number
  activeLotsCount: number
  activeLots: BiomassKPI[]
  historicalLots: BiomassKPI[]
  totalHistoricalBiomass: number
  selectedFarmLabel: string
  selectedProductionUnitLabel: string
  formatStageLabel: (stage: string) => string
}>

function BiomassAnalyzeBody(props: BiomassAnalyzeBodyProps): React.ReactNode {
  const {
    t,
    biomassKPIs,
    performanceSnapshot,
    viewMode,
    isSmallScreen,
    stockLabel,
    totalActiveBiomass,
    totalActiveStock,
    averageWeight,
    averageObservedMortalityRate,
    observedLotsCount,
    averageSurvivalRate,
    survivalLotsCount,
    totalProjectedBiomass,
    totalProjectedBiomassCurrentTrend,
    averageExpectedMortalityRate,
    projectedLotsCount,
    projectedTrendLotsCount,
    activeLotsCount,
    activeLots,
    historicalLots,
    totalHistoricalBiomass,
    selectedFarmLabel,
    selectedProductionUnitLabel,
    formatStageLabel
  } = props

  // Findings #209: a unit whose lots have no weight sample yet still shows
  // its lots' stock in the current view; biomass and weight stay "—".
  if (biomassKPIs.length === 0 && (viewMode !== 'current' || activeLots.length === 0)) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '2px solid #e9ecef'
      }}>
        <h3 style={{ color: '#6c757d', marginBottom: '10px' }}>{t('biomass.empty.noData.title')}</h3>
        <p style={{ color: '#6c757d', margin: 0 }}>
          {t('biomass.empty.noData.subtitle', { farm: selectedFarmLabel, unit: selectedProductionUnitLabel })}
        </p>
      </div>
    )
  }

  if (viewMode === 'current') {
    const weighedActiveLotsCount = activeLots.filter((kpi) => kpi.averageWeightG > 0).length
    const unweighedActiveLotsCount = activeLots.length - weighedActiveLotsCount
    const biomassBreakdownRows = [
      ...[...activeLots].sort((a, b) => b.totalBiomassKg - a.totalBiomassKg).map((kpi) => ({ kpi, isActive: true })),
      ...[...historicalLots].sort((a, b) => b.totalBiomassKg - a.totalBiomassKg).map((kpi) => ({ kpi, isActive: false }))
    ]

    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#059669' }}>{t('biomass.cards.totalActiveBiomass.title')}</h4>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                  {weighedActiveLotsCount > 0 ? `${totalActiveBiomass.toFixed(1)} kg` : '—'}
                </div>
                <div style={{ fontSize: '12px', color: '#059669' }}>{t('biomass.cards.totalActiveBiomass.activeLabel')}</div>
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#6b7280' }}>
                  {totalHistoricalBiomass.toFixed(1)} kg
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{t('biomass.cards.totalActiveBiomass.historicalLabel')}</div>
              </div>
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>{t('biomass.cards.totalActiveBiomass.caption', { count: activeLotsCount })}</div>
            {unweighedActiveLotsCount > 0 ? (
              <div data-testid="biomass-needs-sample" style={{ fontSize: '13px', color: '#92400e', marginTop: '6px' }}>
                {t('biomass.cards.totalActiveBiomass.needsSample', { count: unweighedActiveLotsCount })}
              </div>
            ) : null}
            {biomassBreakdownRows.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '10px', maxHeight: '160px', overflowY: 'auto' }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase' }}>
                  {t('biomass.cards.totalActiveBiomass.breakdownTitle')}
                </div>
                {biomassBreakdownRows.map(({ kpi, isActive }) => (
                  <div
                    key={`${isActive ? 'active' : 'historical'}-${kpi.lotToken}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '13px',
                      padding: '4px 0',
                      gap: '8px'
                    }}
                  >
                    <span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {kpi.lot}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      <span style={{ color: '#111827', fontWeight: 500 }}>{kpi.averageWeightG > 0 ? `${kpi.totalBiomassKg.toFixed(1)} kg` : '—'}</span>
                      <span style={{
                        fontSize: '11px',
                        padding: '1px 6px',
                        borderRadius: '9999px',
                        backgroundColor: isActive ? '#d1fae5' : '#e5e7eb',
                        color: isActive ? '#059669' : '#6b7280'
                      }}>
                        {isActive
                          ? t('biomass.cards.totalActiveBiomass.activeLabel')
                          : t('biomass.cards.totalActiveBiomass.historicalLabel')}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#0284c7' }}>{t('biomass.cards.totalStock.title', { stockLabel })}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0284c7' }}>
              {totalActiveStock.toLocaleString()}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>{t('biomass.cards.totalStock.caption')}</div>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#7c3aed' }}>{t('biomass.cards.avgWeight.title')}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7c3aed' }}>
              {averageWeight > 0 ? `${formatShrimpWeightG(averageWeight)} g` : '—'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>{t('biomass.cards.avgWeight.caption')}</div>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#10b981' }}>{t('biomass.cards.survival.title')}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
              {averageSurvivalRate === null ? '--' : `${averageSurvivalRate.toFixed(2)}%`}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>{t('biomass.cards.survival.caption', { count: survivalLotsCount })}</div>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#b91c1c' }}>{t('biomass.cards.observedMortality.title')}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#b91c1c' }}>
              {averageObservedMortalityRate === null ? '--' : `${averageObservedMortalityRate.toFixed(2)}%`}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
              {t('biomass.cards.observedMortality.caption', { count: observedLotsCount })}
            </div>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#dc2626' }}>{t('biomass.cards.expectedMortality.title')}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
              {averageExpectedMortalityRate === null ? '--' : `${averageExpectedMortalityRate.toFixed(2)}%`}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
              {t('biomass.cards.expectedMortality.caption', { count: projectedLotsCount })}
            </div>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#0f766e' }}>{t('biomass.cards.projectedBiomass.title')}</h4>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f766e' }}>
              {totalProjectedBiomass === null ? '--' : `${totalProjectedBiomass.toFixed(1)} kg`}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
              {t('biomass.cards.projectedBiomass.caption', { count: projectedLotsCount })}
            </div>
          </div>
        </div>

        {projectedLotsCount === 0 ? (
          <div style={{ marginBottom: 24, color: '#6b7280', fontSize: 14 }}>
            {t('biomass.projection.emptyHint')}
          </div>
        ) : null}

        <div
          style={{
            marginBottom: 24,
            padding: 16,
            borderRadius: 12,
            border: '1px solid #fde68a',
            backgroundColor: '#fffbeb'
          }}
        >
          <h4 style={{ margin: '0 0 10px 0', color: '#92400e' }}>{t('biomass.projection.assumptions.title')}</h4>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#78350f', fontSize: 14, display: 'grid', gap: 6 }}>
            <li>{t('biomass.projection.assumptions.observed')}</li>
            <li>{t('biomass.projection.assumptions.expected')}</li>
            <li>{t('biomass.projection.assumptions.stock')}</li>
            <li>{t('biomass.projection.assumptions.biomass')}</li>
          </ul>
        </div>

        <div
          style={{
            marginBottom: 24,
            padding: 16,
            borderRadius: 12,
            border: '1px solid #d1fae5',
            backgroundColor: '#f0fdf4'
          }}
        >
          <h4 style={{ margin: '0 0 10px 0', color: '#065f46' }}>{t('biomass.projection.scenarios.title')}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div style={{ backgroundColor: 'white', border: '1px solid #a7f3d0', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, color: '#065f46', marginBottom: 6 }}>{t('biomass.projection.scenarios.currentTrendLabel')}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#047857' }}>
                {totalProjectedBiomassCurrentTrend === null ? '--' : `${totalProjectedBiomassCurrentTrend.toFixed(1)} kg`}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#065f46' }}>
                {t('biomass.projection.scenarios.currentTrendCaption', { count: projectedTrendLotsCount })}
              </div>
            </div>
            <div style={{ backgroundColor: 'white', border: '1px solid #bae6fd', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, color: '#0c4a6e', marginBottom: 6 }}>{t('biomass.projection.scenarios.expectedLabel')}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0369a1' }}>
                {totalProjectedBiomass === null ? '--' : `${totalProjectedBiomass.toFixed(1)} kg`}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#0c4a6e' }}>
                {t('biomass.projection.scenarios.expectedCaption', { count: projectedLotsCount })}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            marginBottom: 24,
            padding: 16,
            borderRadius: 12,
            border: '1px solid #dbeafe',
            backgroundColor: '#f8fbff'
          }}
        >
          <h4 style={{ margin: '0 0 10px 0', color: '#1d4ed8' }}>{t('biomass.performance.title')}</h4>
          {performanceSnapshot ? (
            <div style={{ display: 'grid', gap: 8, fontSize: 14, color: '#1f2937' }}>
              <div>{t('biomass.performance.asOf', { date: performanceSnapshot.asOfDate })}</div>
              <div>{t('biomass.performance.activeLots', { count: performanceSnapshot.activeLotCount })}</div>
              <div>{t('biomass.performance.currentObserved', { value: performanceSnapshot.currentObservedMortalityRate === null ? '--' : `${performanceSnapshot.currentObservedMortalityRate.toFixed(2)}%` })}</div>
              <div>{t('biomass.performance.previousObserved', { value: performanceSnapshot.previousObservedMortalityRate === null ? '--' : `${performanceSnapshot.previousObservedMortalityRate.toFixed(2)}%` })}</div>
              <div>{t('biomass.performance.expected', { value: performanceSnapshot.currentExpectedMortalityRate === null ? '--' : `${performanceSnapshot.currentExpectedMortalityRate.toFixed(2)}%` })}</div>
              <div>{t('biomass.performance.gap', { value: performanceSnapshot.mortalityGapPts === null ? '--' : `${performanceSnapshot.mortalityGapPts.toFixed(2)} pts` })}</div>
              <div>{t('biomass.performance.trend', { value: t(`biomass.performance.trend.${performanceSnapshot.trend}`) })}</div>
              <div>{t('biomass.performance.samples', { current: performanceSnapshot.currentSampleCount, previous: performanceSnapshot.previousSampleCount })}</div>
              <div>
                {t('biomass.performance.trustSummary', {
                  value: t(`biomass.performance.trustSummary.${performanceSnapshot.trustSummary}`)
                })}
              </div>
              <div>
                {t(`biomass.performance.trustReason.${performanceSnapshot.trustReason}`)}
              </div>
              <details style={{ marginTop: 4 }}>
                <summary
                  style={{ cursor: 'pointer', color: '#1d4ed8', fontSize: 13 }}
                  data-show-label={t('biomass.performance.details.show')}
                  data-hide-label={t('biomass.performance.details.hide')}
                >
                  {t('biomass.performance.details.show')}
                </summary>
                <div style={{ display: 'grid', gap: 6, marginTop: 8, color: '#374151' }}>
                  <div>{t('biomass.performance.sourceContract')}</div>
                  <div>
                    {t('biomass.performance.sourceMode', {
                      currentMode: t(`biomass.performance.sourceMode.${performanceSnapshot.currentObservedSourceMode}`),
                      previousMode: t(`biomass.performance.sourceMode.${performanceSnapshot.previousObservedSourceMode}`)
                    })}
                  </div>
                  <div>
                    {t('biomass.performance.sourceCoverage', {
                      currentLog: performanceSnapshot.currentMortalityLogSampleCount,
                      currentTotal: performanceSnapshot.currentSampleCount,
                      previousLog: performanceSnapshot.previousMortalityLogSampleCount,
                      previousTotal: performanceSnapshot.previousSampleCount
                    })}
                  </div>
                  <div>
                    {t('biomass.performance.sourceCoverageDates', {
                      currentDate: performanceSnapshot.currentMortalityLogLatestDate ?? '--',
                      previousDate: performanceSnapshot.previousMortalityLogLatestDate ?? '--'
                    })}
                  </div>
                  <div>
                    {t('biomass.performance.sourceRecencyRisk', {
                      currentRisk: t(`biomass.performance.sourceRecencyRisk.${performanceSnapshot.currentMortalityLogRecencyRisk}`),
                      previousRisk: t(`biomass.performance.sourceRecencyRisk.${performanceSnapshot.previousMortalityLogRecencyRisk}`)
                    })}
                  </div>
                </div>
              </details>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: '#6b7280' }}>{t('biomass.performance.empty')}</div>
          )}
        </div>

        <h3 style={{ marginBottom: '20px', color: '#374151' }}>{t('biomass.lotDetails.title')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
          {biomassKPIs.slice(0, 10).map((kpi) => (
            <div key={`${kpi.lotToken}-${kpi.date}-${kpi.week}`} style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '2px solid #e5e7eb',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0, color: '#374151' }}>{kpi.lot}</h4>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: kpi.stage === 'Active' ? '#dcfce7' : '#fef3c7',
                  color: kpi.stage === 'Active' ? '#166534' : '#92400e',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {formatStageLabel(kpi.stage)}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                {kpi.weekDisplay}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.biomass')} </span>
                    <span style={{ color: '#059669' }}>{kpi.totalBiomassKg.toFixed(1)} kg</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.stock')} </span>
                    <span style={{ color: '#0284c7' }}>{kpi.fishStock.toLocaleString()}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.avgWeight')} </span>
                    <span style={{ color: '#7c3aed' }}>{formatShrimpWeightG(kpi.averageWeightG)} g</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.projectedBiomass')} </span>
                    <span style={{ color: '#0f766e' }}>{kpi.projectedBiomassKg === null ? '--' : `${kpi.projectedBiomassKg.toFixed(1)} kg`}</span>
                  </div>
                </div>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.mortality')} </span>
                    <span style={{ color: '#dc2626' }}>{kpi.observedMortalityRate === null ? '--' : `${kpi.observedMortalityRate.toFixed(2)}%`}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.survival')} </span>
                    <span style={{ color: '#10b981' }}>{kpi.survivalRate === null ? '--' : `${kpi.survivalRate.toFixed(2)}%`}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.density')} </span>
                    {kpi.stockingDensity === null ? (
                      <span style={{ color: '#6b7280' }} title={t('density.noVolume')}>--</span>
                    ) : (() => {
                      // Findings #151: green / amber from 90 % / red over the lot's table limit.
                      const densityStatus = statusAgainstMaximum(kpi.stockingDensity, kpi.densityLimitKgM3)
                      if (!densityStatus) return <span style={{ color: '#f59e0b' }}>{kpi.stockingDensity.toFixed(1)} kg/m³</span>
                      return (
                        <span
                          style={{ color: CAPACITY_STATUS_COLOR[densityStatus], fontWeight: densityStatus === 'ok' ? 600 : 700 }}
                          title={densityStatus === 'ok' ? undefined : t(densityStatus === 'red' ? 'density.overLimitTitle' : 'density.nearLimitTitle')}
                          data-testid="biomass-density-status"
                          data-status={densityStatus}
                        >
                          {kpi.stockingDensity.toFixed(1)} kg/m³ · {t('density.limitLabel', { limit: kpi.densityLimitKgM3!.toFixed(1) })}
                          {kpi.densityTableName ? ` (${kpi.densityTableName})` : ''}
                          {(() => {
                            const word = capacityStatusWordKey(densityStatus, 'maximum')
                            return word ? ` · ${t(word)}` : ''
                          })()}
                        </span>
                      )
                    })()}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151', cursor: 'help' }} title={t('biomass.lotDetails.metric.fcrInFlightHelp')}>{t('biomass.lotDetails.metric.fcrInFlight')} </span>
                    <span style={{ color: '#7c3aed' }}>{kpi.feedConversionRatioInFlight === null ? '--' : kpi.feedConversionRatioInFlight}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.expectedMortality')} </span>
                    <span style={{ color: '#dc2626' }}>{kpi.expectedMortalityRate === null ? '--' : `${kpi.expectedMortalityRate.toFixed(2)}%`}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.projectedStock')} </span>
                    <span style={{ color: '#0f766e' }}>{kpi.projectedStock === null ? '--' : kpi.projectedStock.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('biomass.lotDetails.metric.temperature')} </span>
                    <span style={{ color: '#0369a1' }}>{kpi.temperatureC === null ? '--' : `${kpi.temperatureC.toFixed(1)}°C`}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  if (viewMode === 'charts') {
    return (
      <>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#374151', marginBottom: '10px' }}>{t('biomass.charts.title')}</h2>
          <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '0' }}>
            {t('biomass.charts.lead')}
          </p>
          {isSmallScreen ? (
            <p style={{ color: '#6b7280', fontSize: '13px', margin: '8px 0 0' }}>
              {t('biomass.charts.tipLandscape')}
            </p>
          ) : null}
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#374151' }}>{t('biomass.charts.avgWeightGrowth.title')}</h3>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <AverageWeightGrowthChart
              points={biomassKPIs.map((kpi) => ({
                date: kpi.date,
                weightG: kpi.averageWeightG,
                lot: kpi.lot
              }))}
              height={isSmallScreen ? 240 : 300}
              width={isSmallScreen ? 760 : 1000}
              showLegend={Boolean(!isSmallScreen)}
            />
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#374151' }}>{t('biomass.charts.stockLevels.title', { stockLabel })}</h3>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            {(() => {
              const filtered = biomassKPIs.filter((kpi) => kpi.fishStock > 0)
              const sorted = [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              const stockData = sorted.map((kpi) => ({
                date: kpi.date,
                stock: kpi.fishStock,
                projectedStock: kpi.projectedStock,
                lot: kpi.lot,
                displayDate: new Date(kpi.date).toLocaleDateString(),
                shortDate: new Date(kpi.date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
                label: `${kpi.lot} - ${new Date(kpi.date).toLocaleDateString()}`
              }))

              if (stockData.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    {t('biomass.charts.stock.noData')}
                  </div>
                )
              }

              const chartWidth = isSmallScreen ? 760 : 1000
              const hasProjectedSeries = stockData.some((entry) => entry.projectedStock !== null)
              const projectedBarHeightSmall = isSmallScreen ? 44 : 52
              const actualBarHeightSmall = isSmallScreen ? 28 : 35
              const barHeight = hasProjectedSeries ? projectedBarHeightSmall : actualBarHeightSmall
              const barSpacing = 10
              const projectedGap = hasProjectedSeries ? 6 : 0
              const actualBarHeight = hasProjectedSeries ? Math.floor((barHeight - projectedGap) / 2) : barHeight
              const projectedBarHeight = hasProjectedSeries ? Math.max(8, barHeight - actualBarHeight - projectedGap) : 0
              const padding = isSmallScreen
                ? { top: 40, right: 90, bottom: 40, left: 120 }
                : { top: 40, right: 120, bottom: 40, left: 200 }
              const chartHeight = stockData.length * (barHeight + barSpacing) + padding.top + padding.bottom

              const maxStock = Math.max(...stockData.map((d) => Math.max(d.stock, d.projectedStock ?? 0)))
              const barMaxWidth = chartWidth - padding.left - padding.right
              const tickRatios = isSmallScreen ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]

              const lotColors: Record<string, string> = {}
              const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316']
              let colorIndex = 0
              stockData.forEach((d) => {
                if (!lotColors[d.lot]) {
                  lotColors[d.lot] = colors[colorIndex % colors.length]
                  colorIndex++
                }
              })

              return (
                <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <text x={chartWidth / 2} y={25} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#374151">
                      {t('biomass.charts.stock.chartTitle', { stockLabel })}
                    </text>

                    {hasProjectedSeries ? (
                      <g>
                        <rect x={padding.left} y={12} width={12} height={12} fill="#3b82f6" rx={2} />
                        <text x={padding.left + 18} y={22} fontSize="11" fill="#374151">{t('biomass.charts.stock.series.actual')}</text>
                        <rect x={padding.left + 110} y={12} width={12} height={12} fill="#0f766e" rx={2} />
                        <text x={padding.left + 128} y={22} fontSize="11" fill="#374151">{t('biomass.charts.stock.series.projected')}</text>
                      </g>
                    ) : null}

                    {stockData.map((d, i) => (
                      <text
                        key={`${d.lot}-${d.date}`}
                        x={padding.left - 10}
                        y={padding.top + i * (barHeight + barSpacing) + barHeight / 2 + 4}
                        textAnchor="end"
                        fontSize={isSmallScreen ? '11' : '12'}
                        fill="#374151"
                      >
                        <title>{d.label}</title>
                        {isSmallScreen ? `${d.lot} ${d.shortDate}` : d.label}
                      </text>
                    ))}

                    {stockData.map((d, i) => {
                      const barWidth = (d.stock / maxStock) * barMaxWidth
                      const projectedBarWidth = d.projectedStock !== null ? (d.projectedStock / maxStock) * barMaxWidth : 0
                      const y = padding.top + i * (barHeight + barSpacing)
                      const projectedY = y + actualBarHeight + projectedGap

                      return (
                        <g key={`${d.lot}-${d.date}`}>
                          <rect
                            x={padding.left}
                            y={y}
                            width={barMaxWidth}
                            height={actualBarHeight}
                            fill="#f3f4f6"
                            rx={4}
                          />
                          <rect
                            x={padding.left}
                            y={y}
                            width={barWidth}
                            height={actualBarHeight}
                            fill={lotColors[d.lot]}
                            rx={4}
                          >
                            <title>{t('biomass.charts.stock.barTooltip', { lot: d.lot, date: d.displayDate, stockLabel, stock: d.stock.toLocaleString() })}</title>
                          </rect>
                          <text
                            x={padding.left + barWidth + 8}
                            y={y + actualBarHeight / 2 + 4}
                            fontSize="12"
                            fill="#374151"
                            fontWeight="500"
                          >
                            {d.stock.toLocaleString()}
                          </text>

                          {hasProjectedSeries ? (
                            <>
                              <rect
                                x={padding.left}
                                y={projectedY}
                                width={barMaxWidth}
                                height={projectedBarHeight}
                                fill="#e2e8f0"
                                rx={4}
                              />
                              {d.projectedStock !== null ? (
                                <>
                                  <rect
                                    x={padding.left}
                                    y={projectedY}
                                    width={projectedBarWidth}
                                    height={projectedBarHeight}
                                    fill="#0f766e"
                                    rx={4}
                                  >
                                    <title>{t('biomass.charts.stock.projectedBarTooltip', { lot: d.lot, date: d.displayDate, stockLabel, stock: d.projectedStock.toLocaleString(undefined, { maximumFractionDigits: 0 }) })}</title>
                                  </rect>
                                  <text
                                    x={padding.left + projectedBarWidth + 8}
                                    y={projectedY + projectedBarHeight / 2 + 4}
                                    fontSize="11"
                                    fill="#0f172a"
                                    fontWeight="500"
                                  >
                                    {d.projectedStock.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                  </text>
                                </>
                              ) : (
                                <text
                                  x={padding.left + 8}
                                  y={projectedY + projectedBarHeight / 2 + 4}
                                  fontSize="11"
                                  fill="#64748b"
                                >
                                  {t('biomass.charts.stock.projectedMissing')}
                                </text>
                              )}
                            </>
                          ) : null}
                        </g>
                      )
                    })}

                    {tickRatios.map((ratio) => {
                      const x = padding.left + ratio * barMaxWidth
                      const value = Math.round(ratio * maxStock)
                      return (
                        <g key={ratio}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={chartHeight - padding.bottom}
                            stroke="#e5e7eb"
                            strokeWidth={1}
                            strokeDasharray="3,3"
                          />
                          <text
                            x={x}
                            y={chartHeight - padding.bottom + 15}
                            textAnchor="middle"
                            fontSize="11"
                            fill="#6b7280"
                          >
                            {value.toLocaleString()}
                          </text>
                        </g>
                      )
                    })}

                    <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="12" fill="#6b7280">
                      {t('biomass.charts.stock.axisTitle')}
                    </text>

                    {Object.entries(lotColors).map(([lot, color], i) => (
                      <g key={lot}>
                        <rect
                          x={chartWidth - padding.right + 30}
                          y={padding.top + i * 20}
                          width={12}
                          height={12}
                          fill={color}
                          rx={2}
                        />
                        <text
                          x={chartWidth - padding.right + 48}
                          y={padding.top + i * 20 + 9}
                          fontSize="11"
                          fill="#374151"
                        >
                          {lot}
                        </text>
                      </g>
                    ))}
                  </svg>
                  {/* v2.1 #22 — when at least one lot has stock data but no
                      lot resolves a projection, mirror the same hint already
                      shown in the Current view (line 805) so operators on
                      the Charts panel see WHY the projected series is
                      missing without switching tabs. */}
                  {!hasProjectedSeries ? (
                    <div
                      data-testid="biomass-charts-projection-empty-hint"
                      style={{
                        marginTop: 12,
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: '1px solid #fde68a',
                        backgroundColor: '#fffbeb',
                        color: '#78350f',
                        fontSize: 13
                      }}
                    >
                      {t('biomass.projection.emptyHint')}
                    </div>
                  ) : null}
                </div>
              )
            })()}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <h3 style={{ marginBottom: '20px', color: '#374151' }}>{t('biomass.historical.title')}</h3>
      <div
        className="table-shell"
        style={{
          overflowX: 'auto',
          border: '2px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.period')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.lot')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.biomassKg')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.stock')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.avgWeightG')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.temperatureC')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.survivalPct')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.mortalityPct')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.expectedMortalityPct')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.projectedStock')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.projectedBiomassKg')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.fcrInFlight')}</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#374151' }}>{t('biomass.historical.column.stage')}</th>
            </tr>
          </thead>
          <tbody>
            {biomassKPIs.map((kpi) => (
              <tr key={`${kpi.lot}-${kpi.date}`} style={{
                backgroundColor: kpi.stage === 'Active' ? '#f0f9ff' : '#f8f9fa',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <td style={{ padding: '12px 16px', fontWeight: '500', color: '#374151' }}>{kpi.weekDisplay}</td>
                <td style={{ padding: '12px 16px', color: '#6b7280' }}>{kpi.lot}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500', color: '#059669' }}>{kpi.totalBiomassKg.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#0284c7' }}>{kpi.fishStock.toLocaleString()}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#7c3aed' }}>{formatShrimpWeightG(kpi.averageWeightG)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#0369a1' }}>{kpi.temperatureC === null ? '--' : `${kpi.temperatureC.toFixed(1)}°C`}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500', color: '#10b981' }}>{kpi.survivalRate === null ? '--' : `${kpi.survivalRate.toFixed(2)}%`}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500', color: '#dc2626' }}>{kpi.observedMortalityRate === null ? '--' : `${kpi.observedMortalityRate.toFixed(2)}%`}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500', color: '#dc2626' }}>{kpi.expectedMortalityRate === null ? '--' : `${kpi.expectedMortalityRate.toFixed(2)}%`}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#0f766e' }}>{kpi.projectedStock === null ? '--' : kpi.projectedStock.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#0f766e' }}>{kpi.projectedBiomassKg === null ? '--' : kpi.projectedBiomassKg.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#7c3aed' }}>{kpi.feedConversionRatioInFlight === null ? '--' : kpi.feedConversionRatioInFlight}</td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: kpi.stage === 'Active' ? '#dcfce7' : '#fef2f2',
                    color: kpi.stage === 'Active' ? '#166534' : '#dc2626'
                  }}>
                    {formatStageLabel(kpi.stage)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

type BiomassAnalyzePanelProps = Readonly<{
  t: (key: any, vars?: Record<string, string | number>) => string
  isVisible: boolean
  viewMode: 'current' | 'historical' | 'charts'
  setViewMode: React.Dispatch<React.SetStateAction<'current' | 'historical' | 'charts'>>
  analyzeBody: React.ReactNode
  stockLabel: string
}>

function BiomassAnalyzePanel(props: BiomassAnalyzePanelProps): React.ReactNode {
  const { t, isVisible, viewMode, setViewMode, analyzeBody, stockLabel } = props
  if (!isVisible) return null

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#374151' }}>{t('biomass.panel.analyze')}</h3>
        <div className="responsive-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewMode('current')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'current' ? '#3b82f6' : '#f3f4f6',
              color: viewMode === 'current' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {t('biomass.view.current')}
          </button>
          <button
            onClick={() => setViewMode('historical')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'historical' ? '#3b82f6' : '#f3f4f6',
              color: viewMode === 'historical' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {t('biomass.view.historical')}
          </button>
          <button
            onClick={() => setViewMode('charts')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'charts' ? '#3b82f6' : '#f3f4f6',
              color: viewMode === 'charts' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {t('biomass.view.charts')}
          </button>
        </div>
      </div>

      {analyzeBody}

      <div
        style={{
          marginTop: '30px',
          padding: '25px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}
      >
        <h3 style={{ marginTop: 0, color: '#1e293b', marginBottom: '20px' }}>{t('biomass.guide.title')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div>
            <h4 style={{ color: '#059669', marginBottom: '10px' }}>{t('biomass.guide.totalBiomass.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('biomass.guide.label.formula')}</strong> {t('biomass.guide.totalBiomass.formulaValue', { stockLabel })}<br />
              <strong>{t('biomass.guide.label.purpose')}</strong> {t('biomass.guide.totalBiomass.purposeValue')}<br />
              <strong>{t('biomass.guide.label.units')}</strong> {t('biomass.guide.totalBiomass.unitsValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#10b981', marginBottom: '10px' }}>{t('biomass.guide.survival.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('biomass.guide.label.formula')}</strong> {t('biomass.guide.survival.formulaValue')}<br />
              <strong>{t('biomass.guide.label.example')}</strong> {t('biomass.guide.survival.exampleValue')}<br />
              <strong>{t('biomass.guide.label.goodRate')}</strong> {t('biomass.guide.survival.goodRateValue')}<br />
              <strong>{t('biomass.guide.label.note')}</strong> {t('biomass.guide.survival.noteValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#f59e0b', marginBottom: '10px' }}>{t('biomass.guide.density.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('biomass.guide.label.formula')}</strong> {t('biomass.guide.density.formulaValue')}<br />
              <strong>{t('biomass.guide.label.units')}</strong> {t('biomass.guide.density.unitsValue')}<br />
              <strong>{t('biomass.guide.label.optimal')}</strong> {t('biomass.guide.density.optimalValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#7c3aed', marginBottom: '10px' }}>{t('biomass.guide.growth.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('biomass.guide.label.averageWeight')}</strong> {t('biomass.guide.growth.averageWeightValue')}<br />
              <strong>{t('biomass.guide.label.monitoring')}</strong> {t('biomass.guide.growth.monitoringValue')}<br />
              <strong>{t('biomass.guide.label.target')}</strong> {t('biomass.guide.growth.targetValue')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

type SamplingDraftState = {
  recordedAt: string
  lotId: string
  averageWeightG: string
  dailyMortality: string
  notes: string
}

type CountDraftState = {
  recordedAt: string
  lotId: string
  method: 'estimate' | 'exact'
  stockCount: string
  notes: string
}

type HarvestDraftState = {
  recordedAt: string
  lotId: string
  harvestKg: string
  harvestCount: string
  notes: string
}

/** Which of the three recent-log lists a per-row action originated from, so
 * `deleteLogRow` can drive the matching section's saving/error state instead
 * of always writing the Sampling slot. */
export type BiomassRecentLogKind = 'sampling' | 'count' | 'harvest'

/** Recent-log lists render the 10 newest by default and reveal older entries
 * in batches of 10 via a "Load more" affordance (mirrors Feed.tsx's
 * `visibleKpiWeeks` pattern). Every row stays in memory — this is a render
 * cap only, so edit/delete remain available on any revealed row. */
const RECENT_LOG_LIST_INITIAL_VISIBLE = 10
const RECENT_LOG_LIST_LOAD_MORE_STEP = 10

type BiomassLogPanelProps = Readonly<{
  t: (key: any, vars?: Record<string, string | number>) => string
  isVisible: boolean
  canEditBiomassLogs: boolean
  /** The selected unit is Inactiva: no new samplings, counts or harvests. */
  unitInactive?: boolean
  statusMessage: string | null
  syncAttentionMessage: string | null
  syncLastResultSummary: { resultLabel: string; attemptedAtLabel: string } | null
  retryingSync: boolean
  refreshing: boolean
  retrySync: () => Promise<void>
  refreshLogs: () => Promise<void>
  lotsLoading: boolean
  availableLots: Array<{ id: string; lotKey?: string | null; stage?: string }>
  interactiveLotCount: number
  lotSelectPlaceholder: string
  stockEmoji: string
  stockLabel: string
  formatStageLabel: (stage: string) => string
  /** Findings #134: deleted lots of the farm (id → name); their records say "Lote eliminado". */
  deletedLotLabels?: ReadonlyMap<string, string>
  /** Findings #140: the row's deaths by observed cause ("Manipulación 4"). */
  describeRowMortalityCauses?: (row: BiomassDataRow) => string
  formatMethodLabel: (method: string) => string
  formatRowDateTime: (raw: string) => string
  logDraft: SamplingDraftState
  setLogDraft: React.Dispatch<React.SetStateAction<SamplingDraftState>>
  logSaving: boolean
  logError: string | null
  saveLogDraft: () => Promise<void>
  countDraft: CountDraftState
  setCountDraft: React.Dispatch<React.SetStateAction<CountDraftState>>
  countSaving: boolean
  countError: string | null
  saveCountDraft: () => Promise<void>
  harvestDraft: HarvestDraftState
  setHarvestDraft: React.Dispatch<React.SetStateAction<HarvestDraftState>>
  harvestSaving: boolean
  harvestError: string | null
  saveHarvestDraft: () => Promise<void>
  /** PR-α3b U5: non-blocking inline banner shown when cumulative harvest for
   * the currently-selected lot reaches the warning threshold (see
   * src/screens/biomass/harvestTransitionThresholds.ts). */
  harvestTransitionWarning: { lotId: string; ratio: number } | null
  /** Non-null while awaiting the second tap on the button-flip confirm CTA
   * for the given lotId (see docs/lot-lifecycle-vs-concerns-audit.md §1.1 X8/X9). */
  pendingHarvestTransitionLotId: string | null
  onConfirmHarvestTransition: (lotId: string) => Promise<void>
  onCancelHarvestTransition: () => void
  recentSamplingRows: BiomassDataRow[]
  recentCountRows: BiomassDataRow[]
  /** Findings #186: "Corregir…" on each count row (its FishStockLog), with a "Corregido" badge. */
  countCorrection?: {
    farmId?: string
    logIdByRowId: Map<string, string>
    correctionCounts: Map<string, number>
    onCorrected: () => Promise<void>
  }
  recentHarvestRows: BiomassDataRow[]
  /** Per-row derived end-of-day fish stock (display-only, mirrors the Database
   * derivation). When `row.fishStock` is blank but the previous known stock
   * and same-day mortality (post MORT-12 overlay) are available, the value
   * here renders as italic gray with a `(derived)` badge. */
  displayedEndOfDayStockByRowId: Map<string, DisplayedEndOfDayStock>
  applyRowToDraft: (row: BiomassDataRow) => void
  deleteLogRow: (rowId: string, kind: BiomassRecentLogKind, reason: string) => Promise<void>
  /** Changes when the active (farm, production unit) context changes; resets
   * every recent-log list back to the default visible count. */
  logListResetKey?: string
  /** How many linked Tier A events a row's delete will also remove — shown in
   * the two-step confirm label so the operator sees the blast radius. */
  getRowLinkedEventCount?: (rowId: string) => number
  onShowMortalityEvents?: (rowId: string) => void
  /** v2.1 finding #35: per-row sampling-event count for the badge that opens
   * the Sampling-events popover. A row whose count is >= 2 indicates the
   * displayed weight is an arithmetic mean of multiple same-day samples. */
  samplingEventCountByRowId?: Record<string, number>
  onShowSamplingEvents?: (rowId: string) => void
}>

const REFUSED_SAMPLING_TYPES = ['weightSampleLog'] as const
const REFUSED_COUNT_TYPES = ['fishStockLog'] as const
const REFUSED_HARVEST_TYPES = ['harvestLog'] as const

export function BiomassLogPanel(props: BiomassLogPanelProps): React.ReactNode {
  const {
    t,
    isVisible,
    canEditBiomassLogs,
    unitInactive = false,
    statusMessage,
    syncAttentionMessage,
    syncLastResultSummary,
    retryingSync,
    refreshing,
    retrySync,
    refreshLogs,
    lotsLoading,
    availableLots,
    interactiveLotCount,
    lotSelectPlaceholder,
    stockEmoji,
    stockLabel,
    formatStageLabel,
    deletedLotLabels,
    describeRowMortalityCauses,
    formatMethodLabel,
    formatRowDateTime,
    logDraft,
    setLogDraft,
    logSaving,
    logError,
    saveLogDraft,
    countDraft,
    setCountDraft,
    countSaving,
    countError,
    saveCountDraft,
    harvestDraft,
    setHarvestDraft,
    harvestSaving,
    harvestError,
    saveHarvestDraft,
    harvestTransitionWarning,
    pendingHarvestTransitionLotId,
    onConfirmHarvestTransition,
    onCancelHarvestTransition,
    recentSamplingRows,
    recentCountRows,
    countCorrection,
    recentHarvestRows,
    displayedEndOfDayStockByRowId,
    applyRowToDraft,
    deleteLogRow,
    logListResetKey,
    getRowLinkedEventCount,
    onShowMortalityEvents,
    samplingEventCountByRowId,
    onShowSamplingEvents
  } = props

  // Findings #172: tag rows whose record the server refused.
  const refusedSamplingRowIds = useRefusedRowIds(REFUSED_SAMPLING_TYPES)
  const refusedCountRowIds = useRefusedRowIds(REFUSED_COUNT_TYPES)
  const refusedHarvestRowIds = useRefusedRowIds(REFUSED_HARVEST_TYPES)
  const [samplingVisibleCount, setSamplingVisibleCount] = useState(RECENT_LOG_LIST_INITIAL_VISIBLE)
  const [countVisibleCount, setCountVisibleCount] = useState(RECENT_LOG_LIST_INITIAL_VISIBLE)
  const [harvestVisibleCount, setHarvestVisibleCount] = useState(RECENT_LOG_LIST_INITIAL_VISIBLE)
  const deleteConfirm = useTwoStepConfirm()

  const renderDeleteControl = (rowId: string, kind: BiomassRecentLogKind, saving: boolean): React.ReactNode => (
    <DeleteProductionRowButton
      t={t}
      testId={`recent-${kind}-delete`}
      armed={deleteConfirm.isPending(rowId)}
      disabled={!canEditBiomassLogs || saving}
      linkedEventCount={getRowLinkedEventCount?.(rowId) ?? 0}
      onArm={() => deleteConfirm.arm(rowId)}
      onCancel={() => deleteConfirm.reset()}
      onConfirm={(reason) => {
        deleteConfirm.reset()
        void deleteLogRow(rowId, kind, reason)
      }}
    />
  )

  useEffect(() => {
    setSamplingVisibleCount(RECENT_LOG_LIST_INITIAL_VISIBLE)
    setCountVisibleCount(RECENT_LOG_LIST_INITIAL_VISIBLE)
    setHarvestVisibleCount(RECENT_LOG_LIST_INITIAL_VISIBLE)
  }, [logListResetKey])

  const renderRecentLogsLoadMore = (
    total: number,
    visibleCount: number,
    onLoadMore: () => void,
    testId: string
  ): React.ReactNode => {
    if (total <= visibleCount) return null
    return (
      <div style={{ marginTop: 12 }}>
        <GradientButton
          type="button"
          size="sm"
          variant="secondary"
          data-testid={testId}
          onClick={onLoadMore}
        >
          {t('biomass.recent.loadMore', { count: total - visibleCount })}
        </GradientButton>
      </div>
    )
  }

  if (!isVisible) return null

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: '#374151' }}>{t('biomass.section.logOperations')}</h3>
        <GradientButton type="button" size="sm" variant="info" onClick={() => void refreshLogs()} loading={refreshing} disabled={refreshing}>
          {t('biomass.button.refreshLogs')}
        </GradientButton>
      </div>

      {statusMessage && computeSyncBannerVisibility({
        hasActionMessage: Boolean(statusMessage),
        hasAttentionMessage: Boolean(syncAttentionMessage),
        hasLastResultSummary: Boolean(syncLastResultSummary)
      }).showActionMessage ? (
        <div
          aria-live="polite"
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            border: '1px solid #bfdbfe',
            backgroundColor: '#eff6ff',
            color: '#1d4ed8'
          }}
        >
          {statusMessage}
        </div>
      ) : null}

      {syncAttentionMessage || syncLastResultSummary ? (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            border: '1px solid #fde68a',
            backgroundColor: '#fffbeb',
            color: '#92400e'
          }}
        >
          {syncAttentionMessage ? <p style={{ margin: 0 }}>{syncAttentionMessage}</p> : null}
          {syncLastResultSummary ? (
            <p style={{ margin: syncAttentionMessage ? '8px 0 0 0' : 0, fontSize: 13 }}>
              {t('biomass.sync.lastResult', { result: syncLastResultSummary.resultLabel })}
              {' '}
              {t('biomass.sync.lastAttempted', { time: syncLastResultSummary.attemptedAtLabel })}
            </p>
          ) : null}
          {syncAttentionMessage ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <GradientButton
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => void retrySync()}
                loading={retryingSync}
                disabled={retryingSync || refreshing}
              >
                {t('biomass.sync.retryAction')}
              </GradientButton>
              <GradientButton
                type="button"
                size="sm"
                variant="info"
                onClick={() => void refreshLogs()}
                loading={refreshing}
                disabled={refreshing || retryingSync}
              >
                {t('biomass.sync.refreshAction')}
              </GradientButton>
            </div>
          ) : null}
        </div>
      ) : null}

      <details
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff'
        }}
      >
        <summary style={{ cursor: 'pointer' }}>
          <h3 style={{ margin: 0, display: 'inline' }}>{t('biomass.details.sampling.title')}</h3>
        </summary>

        {canEditBiomassLogs ? null : (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e' }}>
            {t('biomass.readonly.sampling')}
          </div>
        )}
        {unitInactive ? (
          <div data-testid="biomass-unit-inactive-notice" role="status" style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e', fontSize: 13 }}>
            {t('unit.inactive.captureBlocked')}
          </div>
        ) : null}

        {logError ? (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#991b1b' }}>
            {logError}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12
          }}
        >
          <div>
            <label htmlFor="biomass-log-recorded-at" style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              {t('biomass.field.recordedAt')}
            </label>
            <DateTimeLocalInput
              timeZone={farmTimeZone()}
              id="biomass-log-recorded-at"
              value={logDraft.recordedAt}
              onChangeValue={(value) => setLogDraft((prev) => ({ ...prev, recordedAt: value }))}
              disabled={!canEditBiomassLogs || logSaving || unitInactive}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #cbd5f5',
                backgroundColor: !canEditBiomassLogs || logSaving ? '#f1f5f9' : '#ffffff',
                fontSize: 16
              }}
            />
          </div>

          <FormField
            id="biomass-log-lot"
            label={t('biomass.field.lot')}
            type="select"
            value={logDraft.lotId}
            disabled={!canEditBiomassLogs || logSaving || lotsLoading || interactiveLotCount === 0}
            placeholder={lotSelectPlaceholder}
            options={availableLots.map((lot) => ({
              value: lot.id,
              label: isLotActiveForInteraction(lot.stage)
                ? (lot.lotKey || `Lot ${lot.id}`)
                : `${lot.lotKey || `Lot ${lot.id}`} (${formatStageLabel(String(lot.stage ?? 'Inactive'))})`,
              disabled: !isLotActiveForInteraction(lot.stage)
            }))}
            onChange={(value) => setLogDraft((prev) => ({ ...prev, lotId: String(value) }))}
          />

          <FormField
            id="biomass-log-weight"
            label={t('biomass.field.avgWeightG')}
            type="text"
            value={logDraft.averageWeightG}
            disabled={!canEditBiomassLogs || logSaving || unitInactive}
            inputMode="decimal"
            onChange={(value) => setLogDraft((prev) => ({ ...prev, averageWeightG: String(value ?? '') }))}
          />

          <FormField
            id="biomass-log-mort"
            label={t('biomass.field.dailyMortality')}
            type="number"
            value={logDraft.dailyMortality}
            disabled={!canEditBiomassLogs || logSaving || unitInactive}
            inputMode="numeric"
            step="1"
            preserveNumberAsString
            onChange={(value) => setLogDraft((prev) => ({ ...prev, dailyMortality: String(value ?? '') }))}
          />

          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              id="biomass-log-notes"
              label={t('biomass.field.notes')}
              type="textarea"
              rows={2}
              value={logDraft.notes}
              disabled={!canEditBiomassLogs || logSaving || unitInactive}
              onChange={(value) => setLogDraft((prev) => ({ ...prev, notes: String(value ?? '') }))}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <GradientButton
            type="button"
            variant="success"
            onClick={() => void saveLogDraft()}
            loading={logSaving}
            disabled={!canEditBiomassLogs || logSaving || unitInactive}
          >
            {t('biomass.button.addSamplingRow')}
          </GradientButton>
        </div>

        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#374151' }}>{t('biomass.recent.samplingTitle')}</h4>
          {recentSamplingRows.length === 0 ? (
            <div style={{ color: '#6b7280' }}>{t('biomass.recent.noneSampling')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {recentSamplingRows.slice(0, samplingVisibleCount).map((row) => (
                <div
                  key={row.id}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ minWidth: 240 }}>
                    <div style={{ fontWeight: 700, color: '#111827' }}>
                      {getBiomassRecentRowTitle(row as any, t('biomass.row.unmappedLot'))}<RefusedRowTag show={refusedSamplingRowIds.has(String(row.id))} />
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {formatRowDateTime(row.date)} · {deletedLotLabels?.has(String((row as { lotId?: string }).lotId ?? ''))
                        ? <span data-testid={`biomass-row-deleted-lot-${row.id}`} style={{ fontStyle: 'italic' }}>{t('biomass.row.deletedLot')}</span>
                        : formatStageLabel(row.stage)}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                      {t('biomass.row.avgWeightPrefix')} <b>{row.weight || '—'}</b> g
                      {(() => {
                        const samplingCount = samplingEventCountByRowId?.[String(row.id)] ?? 0
                        if (samplingCount < 2) return null
                        const countLabel = String(samplingCount)
                        return (
                          <button
                            type="button"
                            data-testid="sampling-events-badge"
                            onClick={() => onShowSamplingEvents?.(String(row.id))}
                            title={t('datatable.sampling.events.badgeAction', { count: countLabel })}
                            style={{
                              marginLeft: 8,
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              fontSize: 11,
                              color: '#0f766e',
                              fontStyle: 'italic',
                              cursor: 'pointer',
                              textDecoration: 'underline dotted',
                              textUnderlineOffset: 2
                            }}
                          >
                            ({t('datatable.sampling.averagedFromEventsLabel', { count: countLabel })})
                          </button>
                        )
                      })()}
                      {' · '}{stockLabel}: {(() => {
                        const trimmedStock = (row.fishStock ?? '').trim()
                        if (trimmedStock) return <b>{trimmedStock}</b>
                        const derived = displayedEndOfDayStockByRowId.get(String(row.id))
                        if (derived?.source === 'derived') {
                          return (
                            <span title={t('datatable.fishStock.derivedTitle')} style={{ color: '#64748b', fontStyle: 'italic' }}>
                              <b>{derived.value}</b>{' '}
                              <span style={{ fontSize: 11 }}>({t('datatable.fishStock.derivedLabel')})</span>
                            </span>
                          )
                        }
                        return <b>—</b>
                      })()}
                    </div>
                    {(row.dailyMortality ?? '').trim() ? (
                      <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                        {t('biomass.field.dailyMortality')}: <b>{row.dailyMortality}</b>
                        {(() => {
                          const eventCount = Number.parseInt(String(row.mortalityLogCount ?? ''), 10)
                          if (!Number.isFinite(eventCount) || eventCount <= 1) return null
                          const countLabel = String(eventCount)
                          return (
                            <button
                              type="button"
                              data-testid="mortality-events-badge"
                              onClick={() => onShowMortalityEvents?.(String(row.id))}
                              title={t('datatable.mortality.events.badgeAction', { count: countLabel })}
                              style={{
                                marginLeft: 8,
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                fontSize: 11,
                                color: '#0f766e',
                                fontStyle: 'italic',
                                cursor: 'pointer',
                                textDecoration: 'underline dotted',
                                textUnderlineOffset: 2
                              }}
                            >
                              ({t('datatable.mortality.derivedFromEventsLabel', { count: countLabel })})
                            </button>
                          )
                        })()}
                        {(() => {
                          const causes = describeRowMortalityCauses?.(row) ?? ''
                          return causes ? (
                            <div data-testid={`biomass-row-mortality-causes-${row.id}`} style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                              {t('biomass.row.mortalityCauses', { causes })}
                            </div>
                          ) : null
                        })()}
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <GradientButton
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => applyRowToDraft(row)}
                      disabled={!canEditBiomassLogs || logSaving || unitInactive}
                    >
                      {t('biomass.button.useAsTemplate')}
                    </GradientButton>
                    {renderDeleteControl(row.id, 'sampling', logSaving)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {renderRecentLogsLoadMore(
            recentSamplingRows.length,
            samplingVisibleCount,
            () => setSamplingVisibleCount((current) => Math.min(recentSamplingRows.length, current + RECENT_LOG_LIST_LOAD_MORE_STEP)),
            'recent-sampling-load-more'
          )}
        </div>
      </details>

      <details
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff'
        }}
      >
        <summary style={{ cursor: 'pointer' }}>
          <h3 style={{ margin: 0, display: 'inline' }}>{t('biomass.details.count.title')}</h3>
        </summary>

        {canEditBiomassLogs ? null : (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e' }}>
            {t('biomass.readonly.count')}
          </div>
        )}

        {countError ? (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#991b1b' }}>
            {countError}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12
          }}
        >
          <div>
            <label htmlFor="biomass-count-recorded-at" style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              {t('biomass.field.recordedAt')}
            </label>
            <DateTimeLocalInput
              timeZone={farmTimeZone()}
              id="biomass-count-recorded-at"
              value={countDraft.recordedAt}
              onChangeValue={(value) => setCountDraft((prev) => ({ ...prev, recordedAt: value }))}
              disabled={!canEditBiomassLogs || countSaving || unitInactive}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #cbd5f5',
                backgroundColor: !canEditBiomassLogs || countSaving ? '#f1f5f9' : '#ffffff',
                fontSize: 16
              }}
            />
          </div>

          <FormField
            id="biomass-count-lot"
            label={t('biomass.field.lot')}
            type="select"
            value={countDraft.lotId}
            disabled={!canEditBiomassLogs || countSaving || lotsLoading || interactiveLotCount === 0}
            placeholder={lotSelectPlaceholder}
            options={availableLots.map((lot) => ({
              value: lot.id,
              label: isLotActiveForInteraction(lot.stage)
                ? (lot.lotKey || `Lot ${lot.id}`)
                : `${lot.lotKey || `Lot ${lot.id}`} (${formatStageLabel(String(lot.stage ?? 'Inactive'))})`,
              disabled: !isLotActiveForInteraction(lot.stage)
            }))}
            onChange={(value) => setCountDraft((prev) => ({ ...prev, lotId: String(value) }))}
          />

          <FormField
            id="biomass-count-method"
            label={t('biomass.field.method')}
            type="select"
            value={countDraft.method}
            disabled={!canEditBiomassLogs || countSaving || unitInactive}
            options={[
              { value: 'estimate', label: t('biomass.method.estimate') },
              { value: 'exact', label: t('biomass.method.exact') }
            ]}
            onChange={(value) => setCountDraft((prev) => ({ ...prev, method: value === 'exact' ? 'exact' : 'estimate' }))}
          />

          <FormField
            id="biomass-count-stock"
            label={`${stockEmoji} ${stockLabel}`}
            type="number"
            value={countDraft.stockCount}
            disabled={!canEditBiomassLogs || countSaving || unitInactive}
            inputMode="numeric"
            step="1"
            preserveNumberAsString
            onChange={(value) => setCountDraft((prev) => ({ ...prev, stockCount: String(value ?? '') }))}
          />

          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              id="biomass-count-notes"
              label={t('biomass.field.notes')}
              type="textarea"
              rows={2}
              value={countDraft.notes}
              disabled={!canEditBiomassLogs || countSaving || unitInactive}
              onChange={(value) => setCountDraft((prev) => ({ ...prev, notes: String(value ?? '') }))}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <GradientButton
            type="button"
            variant="success"
            onClick={() => void saveCountDraft()}
            loading={countSaving}
            disabled={!canEditBiomassLogs || countSaving || unitInactive}
          >
            {t('biomass.button.addCountRow')}
          </GradientButton>
        </div>

        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#374151' }}>{t('biomass.recent.countTitle')}</h4>
          {recentCountRows.length === 0 ? (
            <div style={{ color: '#6b7280' }}>{t('biomass.recent.noneCount')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {recentCountRows.slice(0, countVisibleCount).map((row) => (
                <div
                  key={row.id}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ minWidth: 240 }}>
                    <div style={{ fontWeight: 700, color: '#111827' }}>
                      {getBiomassRecentRowTitle(row as any, t('biomass.row.unmappedLot'))}<RefusedRowTag show={refusedCountRowIds.has(String(row.id))} />
                    </div>
                    {countCorrection ? (
                      <CrewRowBadges
                        correctionCount={countCorrection.correctionCounts.get(countCorrection.logIdByRowId.get(String(row.id)) ?? '') ?? 0}
                        actorNames={null}
                        testId={`biomass-count-badges-${row.id}`}
                      />
                    ) : null}
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {formatRowDateTime(row.date)} · {deletedLotLabels?.has(String((row as { lotId?: string }).lotId ?? ''))
                        ? <span data-testid={`biomass-row-deleted-lot-${row.id}`} style={{ fontStyle: 'italic' }}>{t('biomass.row.deletedLot')}</span>
                        : formatStageLabel(row.stage)}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                      {stockLabel}: <b>{row.fishStock || '—'}</b> · {t('biomass.row.methodPrefix')} <b>{(row as any).stockMethod ? formatMethodLabel(String((row as any).stockMethod)) : '—'}</b>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {canEditBiomassLogs && countCorrection?.logIdByRowId.get(String(row.id)) ? (
                      <CrewCorrectionButton
                        resource="fish-stock-log"
                        entityId={countCorrection.logIdByRowId.get(String(row.id)) ?? ''}
                        farmId={countCorrection.farmId}
                        onDone={countCorrection.onCorrected}
                      />
                    ) : null}
                    {renderDeleteControl(row.id, 'count', countSaving)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {renderRecentLogsLoadMore(
            recentCountRows.length,
            countVisibleCount,
            () => setCountVisibleCount((current) => Math.min(recentCountRows.length, current + RECENT_LOG_LIST_LOAD_MORE_STEP)),
            'recent-count-load-more'
          )}
        </div>
      </details>

      <details
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff'
        }}
      >
        <summary style={{ cursor: 'pointer' }}>
          <h3 style={{ margin: 0, display: 'inline' }}>{t('biomass.details.harvest.title')}</h3>
        </summary>

        {canEditBiomassLogs ? null : (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e' }}>
            {t('biomass.readonly.harvest')}
          </div>
        )}

        {harvestError ? (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#991b1b' }}>
            {harvestError}
          </div>
        ) : null}

        {harvestTransitionWarning && harvestTransitionWarning.lotId === harvestDraft.lotId ? (
          <div
            data-testid="biomass-harvest-transition-warning"
            style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e' }}
          >
            {t('biomass.harvest.transition.warningBanner', { ratio: Math.round(harvestTransitionWarning.ratio * 100) })}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12
          }}
        >
          <div>
            <label htmlFor="biomass-harvest-recorded-at" style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              {t('biomass.field.recordedAt')}
            </label>
            <DateTimeLocalInput
              timeZone={farmTimeZone()}
              id="biomass-harvest-recorded-at"
              value={harvestDraft.recordedAt}
              onChangeValue={(value) => setHarvestDraft((prev) => ({ ...prev, recordedAt: value }))}
              disabled={!canEditBiomassLogs || harvestSaving || unitInactive}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #cbd5f5',
                backgroundColor: !canEditBiomassLogs || harvestSaving ? '#f1f5f9' : '#ffffff',
                fontSize: 16
              }}
            />
          </div>

          <FormField
            id="biomass-harvest-lot"
            label={t('biomass.field.lot')}
            type="select"
            value={harvestDraft.lotId}
            disabled={!canEditBiomassLogs || harvestSaving || lotsLoading || interactiveLotCount === 0}
            placeholder={lotSelectPlaceholder}
            options={availableLots.map((lot) => ({
              value: lot.id,
              label: isLotActiveForInteraction(lot.stage)
                ? (lot.lotKey || `Lot ${lot.id}`)
                : `${lot.lotKey || `Lot ${lot.id}`} (${formatStageLabel(String(lot.stage ?? 'Inactive'))})`,
              disabled: !isLotActiveForInteraction(lot.stage)
            }))}
            onChange={(value) => {
              // PR-α3b U5 §4.4: changing the selected lot cancels any pending
              // button-flip confirm for a different lot.
              onCancelHarvestTransition()
              setHarvestDraft((prev) => ({ ...prev, lotId: String(value) }))
            }}
          />

          <FormField
            id="biomass-harvest-kg"
            label={t('biomass.field.harvestKg')}
            type="number"
            value={harvestDraft.harvestKg}
            disabled={!canEditBiomassLogs || harvestSaving || unitInactive}
            inputMode="decimal"
            step="any"
            preserveNumberAsString
            onChange={(value) => setHarvestDraft((prev) => ({ ...prev, harvestKg: String(value ?? '') }))}
          />

          <FormField
            id="biomass-harvest-count"
            label={t('biomass.field.harvestCount')}
            type="number"
            value={harvestDraft.harvestCount}
            disabled={!canEditBiomassLogs || harvestSaving || unitInactive}
            inputMode="numeric"
            step="1"
            preserveNumberAsString
            onChange={(value) => setHarvestDraft((prev) => ({ ...prev, harvestCount: String(value ?? '') }))}
          />

          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              id="biomass-harvest-notes"
              label={t('biomass.field.notes')}
              type="textarea"
              rows={2}
              value={harvestDraft.notes}
              disabled={!canEditBiomassLogs || harvestSaving || unitInactive}
              onChange={(value) => setHarvestDraft((prev) => ({ ...prev, notes: String(value ?? '') }))}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {pendingHarvestTransitionLotId && pendingHarvestTransitionLotId === harvestDraft.lotId ? (
            <>
              <GradientButton
                type="button"
                variant="secondary"
                onClick={onCancelHarvestTransition}
              >
                {t('common.cancel')}
              </GradientButton>
              <GradientButton
                type="button"
                variant="danger"
                onClick={() => void onConfirmHarvestTransition(pendingHarvestTransitionLotId)}
              >
                {t('biomass.harvest.transition.confirmCta')}
              </GradientButton>
            </>
          ) : (
            <GradientButton
              type="button"
              variant="success"
              onClick={() => void saveHarvestDraft()}
              loading={harvestSaving}
              disabled={!canEditBiomassLogs || harvestSaving || unitInactive}
            >
              {t('biomass.button.addHarvestRow')}
            </GradientButton>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#374151' }}>{t('biomass.recent.harvestTitle')}</h4>
          {recentHarvestRows.length === 0 ? (
            <div style={{ color: '#6b7280' }}>{t('biomass.recent.noneHarvest')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {recentHarvestRows.slice(0, harvestVisibleCount).map((row) => (
                <div
                  key={row.id}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ minWidth: 240 }}>
                    <div style={{ fontWeight: 700, color: '#111827' }}>
                      {getBiomassRecentRowTitle(row as any, t('biomass.row.unmappedLot'))}<RefusedRowTag show={refusedHarvestRowIds.has(String(row.id))} />
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {formatRowDateTime(row.date)} · {deletedLotLabels?.has(String((row as { lotId?: string }).lotId ?? ''))
                        ? <span data-testid={`biomass-row-deleted-lot-${row.id}`} style={{ fontStyle: 'italic' }}>{t('biomass.row.deletedLot')}</span>
                        : formatStageLabel(row.stage)}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                      {t('biomass.row.harvestPrefix')} <b>{(row as any).harvestKg || '—'}</b> kg · <b>{(row as any).harvestCount || '—'}</b> {t('biomass.unit.count')}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {renderDeleteControl(row.id, 'harvest', harvestSaving)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {renderRecentLogsLoadMore(
            recentHarvestRows.length,
            harvestVisibleCount,
            () => setHarvestVisibleCount((current) => Math.min(recentHarvestRows.length, current + RECENT_LOG_LIST_LOAD_MORE_STEP)),
            'recent-harvest-load-more'
          )}
        </div>
      </details>
    </div>
  )
}

type BiomassContextSelectorProps = Readonly<{
  t: (key: any, vars?: Record<string, string | number>) => string
  selectedFarmLabel: string
  selectedProductionUnitLabel: string
  farmId: string
  productionUnitId: string
  availableFarms: any[]
  availableProductionUnits: any[]
  biomassKpisCount: number
  onChangeFarm: (value: string) => void
  onChangeProductionUnit: (value: string) => void
}>

function BiomassContextSelector(props: BiomassContextSelectorProps): React.ReactNode {
  const {
    t,
    selectedFarmLabel,
    selectedProductionUnitLabel,
    farmId,
    productionUnitId,
    availableFarms,
    availableProductionUnits,
    biomassKpisCount,
    onChangeFarm,
    onChangeProductionUnit
  } = props

  const summary = farmId && productionUnitId ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap'
      }}
    >
      <div
        style={{
          padding: '10px 15px',
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d7ff',
          borderRadius: '6px',
          fontWeight: 'bold',
          color: '#0056b3',
          flex: '0 0 auto'
        }}
      >
        {t('biomass.summary.lotRecords', { count: biomassKpisCount })}
      </div>
    </div>
  ) : null

  return (
    <>
      <h2>{t('biomass.title')}</h2>

      <CurrentContextBar
        fields={[
          { label: t('biomass.context.facility'), value: selectedFarmLabel },
          { label: t('biomass.context.productionUnit'), value: selectedProductionUnitLabel }
        ]}
      />

      <div className="responsive-toolbar">
        <ContextSelectorBar
          farm={{
            label: t('biomass.context.facility'),
            value: farmId,
            onChange: onChangeFarm,
            placeholder: t('biomass.context.selectFacility'),
            options: availableFarms.map((farm: any) => ({
              key: String(farm.id ?? farm.name ?? ''),
              value: String(farm.id ?? farm.name ?? ''),
              label: farm.name
            }))
          }}
          productionUnit={{
            label: t('biomass.context.productionUnit'),
            value: productionUnitId,
            onChange: onChangeProductionUnit,
            disabled: !farmId || availableProductionUnits.length === 0,
            placeholder: farmId ? t('biomass.context.selectProductionUnit') : t('biomass.context.selectFacilityFirst'),
            options: buildUnitSelectOptions(availableProductionUnits, t('unit.inactive.suffix'))
          }}
          summary={summary}
        />
      </div>
    </>
  )
}

const getLotSelectPlaceholder = (args: {
  productionUnitId: string
  lotsLoading: boolean
  lotsCount: number
  t: (key: any, vars?: Record<string, string | number>) => string
}): string => {
  const { productionUnitId, lotsLoading, lotsCount, t } = args

  if (!productionUnitId) return t('biomass.lot.placeholder.selectUnitFirst')
  if (lotsLoading) return t('biomass.lot.placeholder.loadingLots')
  if (lotsCount === 0) return t('biomass.lot.placeholder.noLots')
  return t('biomass.lot.placeholder.chooseLot')
}

/** Lifetime `FeedLog.feedKg` sum per lot, keyed by `lotId`. No lineage weighting (unlike the Reports `lotCostPerKg` dataset) — a narrower, deliberate simplification for this card; see the FCR-in-flight PRD. */
const buildFeedKgToDateByLotId = (feedLogs: readonly FeedLog[]): Map<string, number> => {
  const totals = new Map<string, number>()
  for (const log of feedLogs) {
    const lotId = typeof log.lotId === 'string' ? log.lotId.trim() : ''
    if (!lotId) continue
    totals.set(lotId, (totals.get(lotId) ?? 0) + parseFloatOrZero(log.feedKg))
  }
  return totals
}

const buildHarvestedLotIds = (harvestLogs: readonly HarvestLog[]): Set<string> => {
  const harvested = new Set<string>()
  for (const log of harvestLogs) {
    const lotId = typeof log.lotId === 'string' ? log.lotId.trim() : ''
    if (!lotId) continue
    if (parseFloatOrZero(log.harvestKg) > 0) harvested.add(lotId)
  }
  return harvested
}

const computeBiomassKpis = (args: {
  farms: any[]
  farmId: string
  productionUnitId: string
  productionRows: BiomassDataRow[]
  availableLots: Array<{ id: string; lotKey?: string | null }>
  waterRows: ProjectionWaterRow[]
  mortalityMatrix: MatrixData
  speciesKey: string
  lineageRows?: readonly LotLineageProjectionRow[]
  feedLogs: readonly FeedLog[]
  harvestLogs: readonly HarvestLog[]
  stockedBiomassByLotId?: ReadonlyMap<string, number | null>
}): { active: BiomassKPI[]; historical: BiomassKPI[] } => {
  const { farms, farmId, productionUnitId, productionRows, availableLots, waterRows, mortalityMatrix, speciesKey, lineageRows, feedLogs, harvestLogs, stockedBiomassByLotId } = args
  const empty = { active: [], historical: [] }
  if (!farmId || !productionUnitId) return empty

  const selectedFarm = resolveSelectedFarm(farms, farmId)
  if (!selectedFarm) return empty

  const selectedProductionUnit = resolveSelectedProductionUnit(selectedFarm, productionUnitId)
  if (!selectedProductionUnit) return empty

  const volumeInM3 = unitVolumeM3(selectedProductionUnit.capacity, selectedProductionUnit.capacityUnit)
  const capacityLotsById = new Map((availableLots as Array<{ id: string; lineageRootId?: string | null; metadata?: Record<string, unknown> | null }>).map((lot) => [lot.id, lot]))
  const { active: activeBasicKpis, historical: historicalBasicKpis } = buildBasicKpis({
    rows: productionRows,
    availableLots,
    volumeInM3,
    // Findings #151: the lot's own Capacidad de carga copy, else the facility default.
    densityLimitForWeight: (averageWeightG, lotId) => {
      const capacity = resolveLotCapacity(selectedFarm, lotId ? capacityLotsById.get(lotId) : null, capacityLotsById)
      return { limitKgM3: capacityDensityLimit(capacity?.table, selectedProductionUnit.type, averageWeightG), tableName: capacity?.table.name ?? null }
    },
    lineageRows,
    feedKgToDateByLotId: buildFeedKgToDateByLotId(feedLogs),
    harvestedLotIds: buildHarvestedLotIds(harvestLogs),
    stockedBiomassByLotId
  })

  // Only the in-roster (`active`) partition runs through survival-rate /
  // mortality-projection enrichment — that pipeline's shape and behavior are
  // unchanged from pre-U2. `historical` (out-of-roster) rows only need a
  // chronological sort for the card's Historico breakdown; running them
  // through grouping/survival/projection isn't part of this PR's scope.
  const groups = groupKpisByLotToken(activeBasicKpis)
  const withRates = applySurvivalRates(groups)
  const withProjection = applyMortalityProjection({
    kpis: withRates,
    waterRows,
    mortalityMatrix,
    speciesKey,
    availableLots
  })

  return {
    active: sortBiomassKpisChronologically(withProjection).reverse(),
    historical: sortBiomassKpisChronologically(historicalBasicKpis).reverse()
  }
}

export type HarvestTransitionOutcome =
  | { signal: 'below' }
  | { signal: 'warning'; ratio: number }
  | {
      signal: 'total'
      ratio: number
      lotUpdatePatch: {
        stage: 'Harvested'
        harvestDate: string
        metadata: Record<string, unknown>
      }
    }

const sumHarvestKgForLot = (lotId: string, harvestLogs: readonly HarvestLog[]): number => (
  harvestLogs
    .filter((log) => log.lotId === lotId)
    .reduce((total, log) => {
      const parsed = parseHumanNumber(String(log.harvestKg ?? ''), { allowDecimal: true, requireInteger: false })
      return total + (parsed.ok ? parsed.value : 0)
    }, 0)
)

/**
 * PR-α3b U5: post-`harvestLogApi.create`, decides whether the just-saved
 * harvest should surface a non-blocking warning banner or a button-flip
 * confirm for closing the lot (`Lot.stage: 'Harvested'`).
 *
 * Pure and side-effect free — `saveHarvestDraft` calls this, then does the
 * actual state-setting / `lotApi.update` I/O. Exported so the decision logic
 * (trigger source + threshold wiring + metadata attribution) is directly
 * unit-testable without mounting the full `Biomas` component tree.
 *
 * `newHarvestLog` is summed in on top of `harvestLogs` (the pre-save state)
 * rather than relying on `harvestLogs` already containing it, since
 * `setHarvestLogs` hasn't run yet at the point `saveHarvestDraft` calls this.
 */
export const resolveHarvestTransitionOutcome = (params: {
  lotId: string
  harvestLogs: readonly HarvestLog[]
  newHarvestLog: HarvestLog
  productionRows: readonly BiomassDataRow[]
  mortalityLogs: readonly MortalityLog[]
  weightSampleLogs: readonly WeightSampleLog[]
  fishStockLogs: readonly FishStockLog[]
  harvestDate: string
}): HarvestTransitionOutcome => {
  const cumulativeHarvestKg = sumHarvestKgForLot(params.lotId, params.harvestLogs)
    + sumHarvestKgForLot(params.lotId, [params.newHarvestLog])

  const biomassByLot = computeCurrentBiomassPerLot({
    productionRows: params.productionRows,
    mortalityLogs: params.mortalityLogs,
    weightSampleLogs: params.weightSampleLogs,
    fishStockLogs: params.fishStockLogs
  })
  const currentBiomassKg = biomassByLot.get(params.lotId)?.biomassKg

  const signal = evaluateHarvestTransitionSignal(cumulativeHarvestKg, currentBiomassKg)
  if (signal === 'below') return { signal: 'below' }

  const ratio = typeof currentBiomassKg === 'number' && currentBiomassKg > 0
    ? cumulativeHarvestKg / currentBiomassKg
    : 0

  if (signal === 'warning') return { signal: 'warning', ratio }

  return {
    signal: 'total',
    ratio,
    lotUpdatePatch: {
      stage: 'Harvested',
      harvestDate: params.harvestDate,
      metadata: {
        source: 'harvest-auto',
        harvestLogId: params.newHarvestLog.id,
        ...(typeof params.newHarvestLog.harvestCount === 'number'
          ? { harvestCount: params.newHarvestLog.harvestCount }
          : {})
      }
    }
  }
}

export default function Biomas() {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useT()
  const principal = useStoredPrincipal()
  const canEditBiomassLogs = !principal || principal.role === 'editor' || principal.role === 'admin'
  const importReviewState = useMemo(() => parseImportWorkspaceReviewRouteState(location.search), [location.search])
  const importReviewNotice = useMemo(() => {
    if (importReviewState.importSource !== 'imports') return null
    return buildImportWorkspaceReviewNotice({
      rowLabel: 'mortality log',
      importedRows: importReviewState.importedRows,
      skippedRows: importReviewState.skippedRows,
      queuedRows: importReviewState.queuedRows,
      localOnlyRows: importReviewState.localOnlyRows,
      blockedRows: importReviewState.blockedRows,
      syncErrorMessage: importReviewState.syncErrorMessage,
      syncState: importReviewState.syncState
    })
  }, [importReviewState])

  const initialActiveContext = useMemo(() => loadActiveContextSelection(), [])

  const [farmId, setFarmId] = useState<string>('')
  // Findings #134: records of a deleted lot say "Lote eliminado", not "Activo".
  const [deletedLotLabels, setDeletedLotLabels] = useState<Map<string, string>>(new Map())
  useEffect(() => {
    if (!farmId) return
    let cancelled = false
    void loadDeletedLotLabels(farmId).then((labels) => {
      if (!cancelled) setDeletedLotLabels(labels)
    })
    return () => {
      cancelled = true
    }
  }, [farmId])
  const [productionUnitId, setProductionUnitId] = useState<string>('')
  // Findings #200: rows of a deleted lot stay saved but are left out here.
  const deletedLotFarmIds = useMemo(() => (farmId ? [farmId] : []), [farmId])
  const isDeletedLotRow = useDeletedLotRowMatcher(deletedLotFarmIds)
  const [biomassKPIs, setBiomassKPIs] = useState<BiomassKPI[]>([])
  // U2 (docs/lot-lifecycle-vs-concerns-audit.md §2 M-Y9-agg): out-of-roster
  // KPIs (moved/soft-deleted lots) that would otherwise be dropped by the
  // active-only `biomassKPIs` pipeline. Feeds the "Historico" bucket of the
  // "Biomasa activa total" card via `biomassSummary`.
  const [historicalBiomassKPIs, setHistoricalBiomassKPIs] = useState<BiomassKPI[]>([])
  const [productionRows, setProductionRows] = useState<BiomassDataRow[]>([])
  const [storedMortalityLogs, setMortalityLogs] = useState<MortalityLog[]>([])
  // Findings #207: a deleted lot's logs never count for an active lot with the
  // same name (overlays match by day and lot name). The stored lists stay for
  // sync status.
  const mortalityLogs = useMemo(() => withoutDeletedLotLogs(storedMortalityLogs, isDeletedLotRow), [storedMortalityLogs, isDeletedLotRow])
  // Findings #140: the entry's deaths by observed cause (Crew Mode code).
  const describeRowMortalityCauses = useCallback(
    (row: BiomassDataRow) => describeMortalityCauses(findAllLinkedMortalityLogsForRow(row, mortalityLogs), t),
    [mortalityLogs, t]
  )
  const [storedWeightSampleLogs, setWeightSampleLogs] = useState<WeightSampleLog[]>([])
  const weightSampleLogs = useMemo(() => withoutDeletedLotLogs(storedWeightSampleLogs, isDeletedLotRow), [storedWeightSampleLogs, isDeletedLotRow])
  // T3-A-07a: mirror WSL/MortalityLog state hook for FishStockLog so the
  // fish-stock cell (`row.fishStock`) can be overlay-populated from server-
  // backed events (T3-A-03 bridge). Read-path wiring only; ProductionRecord.
  // fishStock stays authoritative for rows without matching events until
  // T3-A-07b retires the column.
  const [storedFishStockLogs, setFishStockLogs] = useState<FishStockLog[]>([])
  const fishStockLogs = useMemo(() => withoutDeletedLotLogs(storedFishStockLogs, isDeletedLotRow), [storedFishStockLogs, isDeletedLotRow])
  // T3-D-03: mirror the FishStockLog state hook for HarvestLog so the
  // Cosecha panel's write side produces a server-backed event stream, and
  // the row-level `harvestKg`/`harvestCount` cache can be overlay-composed
  // via SUM-PER-DAY semantics (T3-D-02 bridge). Read-path wiring here keeps
  // the `ProductionRecord` cache columns authoritative for rows without
  // matching events; T3-D-04+ will retire the cache columns.
  const [storedHarvestLogs, setHarvestLogs] = useState<HarvestLog[]>([])
  const harvestLogs = useMemo(() => withoutDeletedLotLogs(storedHarvestLogs, isDeletedLotRow), [storedHarvestLogs, isDeletedLotRow])
  // FCR-in-flight PRD (2026-09-11): read-only FeedLog fetch, mirroring the
  // HarvestLog pattern above. Biomass never writes FeedLog (Feed.tsx owns
  // that) — this is purely a lifetime feed-kg-per-lot input for the
  // in-flight FCR card, no optimistic-create wiring needed.
  const [feedLogs, setFeedLogs] = useState<FeedLog[]>([])
  // PR-α3b U5: harvest-driven Lot.stage auto-transition. `pendingHarvestTransitionLotId`
  // drives the button-flip confirm (non-null while awaiting a second tap on the
  // "Confirmar cierre del lote" CTA); `pendingHarvestTransitionPatch` carries the
  // resolved lotApi.update payload for that lot so the confirm tap doesn't need to
  // re-derive it. `harvestTransitionWarning` drives the non-blocking inline banner.
  // No `globalThis.confirm()` — silently suppressed on iOS Chrome (finding #83) and
  // this is the mobile field-capture profile.
  const [pendingHarvestTransitionLotId, setPendingHarvestTransitionLotId] = useState<string | null>(null)
  const [pendingHarvestTransitionPatch, setPendingHarvestTransitionPatch] = useState<{
    stage: 'Harvested'
    harvestDate: string
    metadata: Record<string, unknown>
  } | null>(null)
  const [harvestTransitionWarning, setHarvestTransitionWarning] = useState<{ lotId: string; ratio: number } | null>(null)
  const [mortalityEventsPopoverRowId, setMortalityEventsPopoverRowId] = useState<string | null>(null)
  // v2.1 finding #60 (WSL-08, 2026-07-01): per-row sampling history moved
  // off the localStorage sidecar entirely \u2014 the popover reads its events
  // from the server-backed `WeightSampleLog` table (WSL-05) and the header
  // row count derives from the daily rollup overlay (WSL-06). There is no
  // longer a client-side aggregate to hold in component state.
  const [samplingEventsPopoverRowId, setSamplingEventsPopoverRowId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'current' | 'historical' | 'charts'>('current')
  const [availableFarms, setAvailableFarms] = useState<any[]>([])
  const [availableProductionUnits, setAvailableProductionUnits] = useState<any[]>([])
  const [projectionWaterRows, setProjectionWaterRows] = useState<ProjectionWaterRow[]>([])
  const [mortalityMatrixData, setMortalityMatrixData] = useState<MatrixData>(() => getDefaultMatrixForSpecies(loadStoredSpecies()))
  const [dataRevision, setDataRevision] = useState(0)
  const [productionTableStatusMessage, setProductionTableStatusMessage] = useState<string | null>(null)
  const [productionSyncStatus, setProductionSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [productionSyncLastOutcome, setProductionSyncLastOutcome] = useState<(BiomassSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [retryingProductionSync, setRetryingProductionSync] = useState(false)
  const [productionTableRefreshing, setProductionTableRefreshing] = useState(false)
  const productionTableStatusVersionRef = useRef(0)
  const pendingProductionActionRef = useRef<'saved' | 'deleted' | null>(null)
  const pendingProductionRowIdsRef = useRef<Set<string>>(new Set())
  // Mirror productionSyncStatus into a ref so applyProductionTableStatus can
  // read the freshest outbox state when reconciling the per-mutation toast
  // (kills v2.1 #21 contradictory label). Keeping it as a ref avoids
  // re-creating the callback on every status tick.
  const productionSyncStatusRef = useRef<EntityBatchSyncStatus | null>(null)
  useEffect(() => {
    productionSyncStatusRef.current = productionSyncStatus
  }, [productionSyncStatus])

  // findings #108: once the outbox reports everything synced, a leftover
  // "… pendiente" save message turns into "… se sincronizó" instead of
  // contradicting the sync banner.
  useEffect(() => {
    if (productionSyncStatus?.syncState !== 'synced') return
    setProductionTableStatusMessage((current) => {
      if (current === t('biomass.status.savedPending')) return t('biomass.status.savedSynced')
      if (current === t('biomass.status.deletedPending')) return t('biomass.status.deletedSynced')
      return current
    })
  }, [productionSyncStatus, t])

  // v2.1 finding #61 (Phase 4 Session 2 Block A4 follow-on): after the
  // entity-mutation `syncPromise` resolves, `applyProductionTableStatus`
  // needs to refresh `productionSyncStatus` so the attention banner
  // reconciles from 'pending' → 'synced'. Without this, the operator
  // sees "los cambios de biomasa están en cola y siguen pendientes de
  // sincronización" forever even though the outbox drained successfully
  // and Prisma confirms the data landed (operator quote, block a4.txt
  // 1:45). The combination with #58 — which now suppresses the
  // "guardado localmente" toast whenever the attention banner is
  // showing — turns the stale-banner gap into a "save appears stuck"
  // bug, so the post-drain refresh is wired through a fn-ref because
  // `refreshCurrentProductionSyncStatus` is defined later in the file
  // than `applyProductionTableStatus` and we want to avoid a hoisting
  // re-order that touches half the screen's effect graph.
  const refreshProductionSyncStatusFnRef = useRef<() => Promise<EntityBatchSyncStatus | null>>(async () => null)

  const {
    lots: availableLots,
    loading: lotsLoading
  } = useLots({ farmId, productionUnitId, enabled: Boolean(farmId && productionUnitId) })

  // Fase 3 (docs/tier-a-merge-consumer-mapping.md §2.6): enriches the
  // basicKpis' canonicalLotIdByToken fallback (buildBasicKpis) so a legacy
  // token belonging to a cross-family-merged lot still resolves current
  // biomass through the surviving lot. Farm-scoped, fails open to [].
  const { rows: lotLineageRows } = useLotLineageProjection({ farmId, enabled: Boolean(farmId) })

  // tier-a-move-transfer-location-addendum.md: productionRows/mortalityLogs/
  // weightSampleLogs/fishStockLogs above are all scoped to this single
  // (farmId, productionUnitId) -- a lot transferred here never has its
  // pre-transfer history fetched at all, so the KPI cards would show it as
  // having no history right after every transfer. Fetches a small, targeted
  // supplement only when a lot in view has actually moved.
  const activeLotIdsForSupplement = useMemo(
    () => availableLots.map((lot) => String(lot.id ?? '')),
    [availableLots]
  )
  const crossFarmSupplement = useCrossFarmLotHistorySupplement({
    farmId,
    productionUnitId,
    lotIds: activeLotIdsForSupplement,
    enabled: Boolean(farmId && productionUnitId)
  })
  // Overlaid in isolation from its own logs only, then concatenated onto
  // `samplingRows` below (already-overlaid) rather than mixed into the
  // primary overlay chain's inputs -- the mortality/weight/fishStock
  // bridges match by `(date, lot label)`, and merging two farms' raw logs
  // into one overlay pass would risk exactly the cross-context collision
  // `scopeLogsToLotBucket` (A1.1 Risk #2) exists to prevent.
  const crossFarmSupplementOverlaid = useMemo(
    () => overlayLotStageOnProductionRows(
      overlayFishStockOnProductionRows(
        overlayWeightSamplesOnProductionRows(
          overlayMortalityLogsOnProductionRows(dropEmptySameDayDuplicateRows(crossFarmSupplement.extraProductionRows), crossFarmSupplement.extraMortalityLogs),
          crossFarmSupplement.extraWeightSampleLogs
        ),
        crossFarmSupplement.extraFishStockLogs
      ),
      availableLots
    ),
    [crossFarmSupplement, availableLots]
  )

  const interactiveLots = useMemo(() => filterInteractiveLots(availableLots), [availableLots])
  // T3-A-07a: chain the FishStockLog overlay after WSL/MORT-12 so
  // `row.fishStock` resolves to the latest FishStockLog `countedStock` on
  // the row's (calendar date, lot) when events exist.
  // T3-D-03: chain the HarvestLog overlay after the biology overlays so
  // `row.harvestKg` / `row.harvestCount` resolve to the SUM of
  // contributing HarvestLog events on the row's (calendar date, lot).
  // T3-D-04b: chain the LotStage overlay OUTERMOST so `row.stage` reflects
  // the LOT'S CURRENT stage (`Lot.stage` on the identity record) instead
  // of the write-time snapshot on the row or the `metadata.stage` value
  // event bridges wrote. Chain order:
  //   mortality → weight → fishStock → harvest → lotStage (outermost).
  // Keeps authority-first per the same pipeline MORT-12 / WSL-06
  // established. Rows without matching events (or without a matching
  // lotId) fall through with referential equality preserved so React
  // memo boundaries stay stable.
  const samplingRows = useMemo(
    () => overlayLotStageOnProductionRows(
      overlayHarvestOnProductionRows(
        overlayFishStockOnProductionRows(
          overlayWeightSamplesOnProductionRows(
            overlayMortalityLogsOnProductionRows(
              dropEmptySameDayDuplicateRows(productionRows).filter((row) => !isDeletedLotRow(row, productionUnitId)),
              mortalityLogs
            ),
            weightSampleLogs
          ),
          fishStockLogs
        ),
        harvestLogs
      ),
      availableLots
    ),
    [productionRows, mortalityLogs, weightSampleLogs, fishStockLogs, harvestLogs, availableLots, isDeletedLotRow, productionUnitId]
  )

  const [logDraft, setLogDraft] = useState<SamplingDraftState>(() => ({
    recordedAt: new Date().toISOString(),
    lotId: '',
    averageWeightG: '',
    dailyMortality: '',
    notes: ''
  }))
  const [logSaving, setLogSaving] = useState(false)
  const [logError, setLogError] = useState<string | null>(null)

  const [countDraft, setCountDraft] = useState<CountDraftState>(() => ({
    recordedAt: new Date().toISOString(),
    lotId: '',
    method: 'estimate',
    stockCount: '',
    notes: ''
  }))
  const [countSaving, setCountSaving] = useState(false)
  const [countError, setCountError] = useState<string | null>(null)

  const [harvestDraft, setHarvestDraft] = useState<HarvestDraftState>(() => ({
    recordedAt: new Date().toISOString(),
    lotId: '',
    harvestKg: '',
    harvestCount: '',
    notes: ''
  }))
  const [harvestSaving, setHarvestSaving] = useState(false)
  const [harvestError, setHarvestError] = useState<string | null>(null)

  // v2.1 finding #54: register a `beforeunload` warning whenever any of the
  // three Biomass entry forms (Sampling / Count / Harvest) carries
  // operator-typed input. Without this, a single accidental Chrome Android
  // pull-to-refresh gesture wipes the draft silently. Option (A) from the
  // finding — cheap defensive layer. Tenant-scoped draft persistence
  // (option B) is parked for v2.2 across all forms.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const isDirty = isAnyBiomassDraftDirty({
      sampling: logDraft,
      count: countDraft,
      harvest: harvestDraft
    })
    if (!isDirty) return

    const handler = (event: BeforeUnloadEvent) => {
      // Modern browsers honour `preventDefault()`; legacy ones require
      // `returnValue` to be set to a non-empty string. We set both to keep
      // Chrome Android (the rehearsal-target surface) consistent.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [logDraft, countDraft, harvestDraft])

  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    const win = getWindowRef()
    if (!win) return false
    return win.matchMedia('(max-width: 430px)').matches
  })

  const [activePanel, setActivePanel] = useState<'log' | 'analyze'>('log')

  useEffect(() => {
    const win = getWindowRef()
    if (!win) return
    const media = win.matchMedia('(max-width: 430px)')
    const onChange = () => setIsSmallScreen(media.matches)

    onChange()

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange)
      return () => media.removeEventListener('change', onChange)
    }

    // Safari iOS < 14
    // eslint-disable-next-line deprecation/deprecation
    media.addListener(onChange)
    // eslint-disable-next-line deprecation/deprecation
    return () => media.removeListener(onChange)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadFarms = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return

      setAvailableFarms(farms)

      // Load previously selected farm and production unit
      const savedFarmToken = readScopedString('biomass-selected-farm')
      const savedProductionUnitToken = readScopedString('biomass-selected-production-unit')

      const activeContext = loadActiveContextSelection()

      const farmToken = importReviewState.farmId || activeContext.farmId || savedFarmToken || ''
      const resolvedFarm = farmToken ? findFarmByIdOrName(farms, farmToken) : undefined
      const nextFarmId = String(resolvedFarm?.id ?? farms?.[0]?.id ?? '')
      setFarmId(nextFarmId)

      const selectedFarm = resolvedFarm ?? farms.find((farm: any) => String(farm?.id ?? '') === nextFarmId)
      const unitToken = importReviewState.productionUnitId || activeContext.productionUnitId || savedProductionUnitToken || ''
      const resolvedProductionUnit = selectedFarm && unitToken
        ? findProductionUnitByIdOrName(selectedFarm, unitToken)
        : undefined
      setProductionUnitId(String(resolvedProductionUnit?.id ?? ''))
    }

    void loadFarms()

    return () => {
      cancelled = true
    }
  }, [importReviewState.farmId, importReviewState.productionUnitId])

  useEffect(() => {
    const { units, nextProductionUnitId } = resolveUnitsAndProductionUnitSelection({
      farmId,
      availableFarms,
      productionUnitId,
      preferredProductionUnitId: initialActiveContext.productionUnitId
    })

    setAvailableProductionUnits(units)
    if (nextProductionUnitId !== productionUnitId) setProductionUnitId(nextProductionUnitId)
  }, [farmId, productionUnitId, availableFarms, initialActiveContext.productionUnitId])

  useEffect(() => {
    if (!farmId) return
    writeScopedString('biomass-selected-farm', farmId)
    persistActiveContextSelection({ farmId })
  }, [farmId])

  // Save production unit selection
  useEffect(() => {
    if (productionUnitId) {
      writeScopedString('biomass-selected-production-unit', productionUnitId)
      persistActiveContextSelection({ productionUnitId })
    }
  }, [productionUnitId])

  const productionContext = useMemo<ProductionUnitTableContext | null>(() => {
    if (!farmId || !productionUnitId) return null
    const selectedFarm = availableFarms.find((farm: any) => asStableIdString(farm?.id) === farmId)
    const selectedProductionUnit = selectedFarm?.productionUnits?.find((unit: any) => asStableIdString(unit?.id) === productionUnitId)
    if (!selectedFarm || !selectedProductionUnit) return null

    return {
      farmId: asStableIdString(selectedFarm.id),
      productionUnitId: asStableIdString(selectedProductionUnit.id),
      farmName: String(selectedFarm.name ?? ''),
      productionUnitName: String(selectedProductionUnit.name ?? '')
    }
  }, [farmId, productionUnitId, availableFarms])

  const getProductionTableStatusKey = useCallback((
    verb: 'saved' | 'deleted',
    state: EntityMutationSyncState
  ): TranslationKey => {
    if (state === 'synced') return `biomass.status.${verb}Synced`
    if (state === 'offline') return `biomass.status.${verb}Offline`
    return `biomass.status.${verb}Pending`
  }, [])

  const trackProductionRowIds = useCallback((rowIds: string[], mode: 'add' | 'remove'): number => {
    const next = new Set(pendingProductionRowIdsRef.current)
    rowIds.forEach((rowId) => {
      const stableId = String(rowId ?? '').trim()
      if (!stableId) return
      if (mode === 'add') next.add(stableId)
      else next.delete(stableId)
    })
    pendingProductionRowIdsRef.current = next
    return next.size
  }, [])

  const applyProductionTableStatus = useCallback((
    verb: 'saved' | 'deleted',
    rowIds: string[],
    result: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    const stableRowIds = rowIds.map((rowId) => String(rowId ?? '').trim()).filter(Boolean)
    const nextVersion = productionTableStatusVersionRef.current + 1
    productionTableStatusVersionRef.current = nextVersion

    if (result.syncState === 'synced') {
      const remaining = trackProductionRowIds(stableRowIds, 'remove')
      if (remaining === 0) {
        pendingProductionActionRef.current = null
      }
    } else {
      trackProductionRowIds(stableRowIds, 'add')
      pendingProductionActionRef.current = verb
    }

    setProductionTableStatusMessage(t(getProductionTableStatusKey(
      verb,
      reconcileSyncMutationState({
        mutationSyncState: result.syncState,
        outboxStatus: productionSyncStatusRef.current
      })
    )))

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (productionTableStatusVersionRef.current !== nextVersion) return

      if (nextState === 'synced') {
        const remaining = trackProductionRowIds(stableRowIds, 'remove')
        if (remaining === 0) {
          pendingProductionActionRef.current = null
        }
      } else {
        trackProductionRowIds(stableRowIds, 'add')
        pendingProductionActionRef.current = verb
      }

      setProductionTableStatusMessage(t(getProductionTableStatusKey(
        verb,
        reconcileSyncMutationState({
          mutationSyncState: nextState,
          outboxStatus: productionSyncStatusRef.current
        })
      )))

      // v2.1 finding #61: reconcile the attention-banner snapshot so it
      // flips from 'pending' to 'synced' immediately after the drain.
      // Without this, finding #58's `showActionMessage` rule keeps the
      // stale attention banner pinned while suppressing the post-save
      // toast — operator-visible regression is "save appears stuck".
      void refreshProductionSyncStatusFnRef.current()
    })
  }, [getProductionTableStatusKey, t, trackProductionRowIds])

  const clearProductionTableStatus = useCallback(() => {
    productionTableStatusVersionRef.current += 1
    pendingProductionActionRef.current = null
    pendingProductionRowIdsRef.current = new Set()
    setProductionTableStatusMessage(null)
  }, [])

  const refreshCurrentProductionRows = useCallback(async (options?: { forceRemote?: boolean }): Promise<boolean> => {
    if (!productionContext) {
      setProductionRows([])
      return false
    }

    try {
      await refreshProductionUnitTableFromApi(productionContext, { forceRemote: options?.forceRemote })
      setDataRevision((value) => value + 1)
      return true
    } catch {
      return false
    }
  }, [productionContext])

  const refreshCurrentMortalityLogs = useCallback(async (options?: {
    preferLocal?: boolean
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!productionContext) {
      setMortalityLogs([])
      return false
    }

    try {
      const logs = await mortalityLogApi.list({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId
      }, {
        preferLocal: options?.preferLocal,
        forceRemote: options?.forceRemote
      })
      setMortalityLogs(logs)
      return true
    } catch {
      if (!options?.preferLocal) {
        setMortalityLogs([])
      }
      return false
    }
  }, [productionContext])

  // v2.1 finding #60 (WSL-06): mirror the mortality list-refresh path for
  // WeightSampleLog so overlay + badge count reflect the server-side event
  // set (cross-device authoritative). Kept intentionally minimal — no
  // separate sync-status wiring in this slice; the outbox verb landed in
  // WSL-02 already reports through the production sync-status pipeline.
  const refreshCurrentWeightSampleLogs = useCallback(async (options?: {
    preferLocal?: boolean
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!productionContext) {
      setWeightSampleLogs([])
      return false
    }

    try {
      const logs = await weightSampleLogApi.list({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId
      }, {
        preferLocal: options?.preferLocal,
        forceRemote: options?.forceRemote
      })
      setWeightSampleLogs(logs)
      return true
    } catch {
      if (!options?.preferLocal) {
        setWeightSampleLogs([])
      }
      return false
    }
  }, [productionContext])

  // T3-A-07a: mirror the WSL/MortalityLog list-refresh path for FishStockLog
  // so the fish-stock overlay reflects the server-side event set cross-
  // device. Same offline-preferred / forceRemote contract as WSL. No
  // separate sync-status wiring in this slice — the outbox verb landed in
  // T3-A-03 already reports through the production sync-status pipeline.
  const refreshCurrentFishStockLogs = useCallback(async (options?: {
    preferLocal?: boolean
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!productionContext) {
      setFishStockLogs([])
      return false
    }

    try {
      const logs = await fishStockLogApi.list({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId
      }, {
        preferLocal: options?.preferLocal,
        forceRemote: options?.forceRemote
      })
      setFishStockLogs(logs)
      return true
    } catch {
      if (!options?.preferLocal) {
        setFishStockLogs([])
      }
      return false
    }
  }, [productionContext])

  // T3-D-03: mirror `refreshCurrentFishStockLogs` for HarvestLog so the
  // harvest overlay reflects the server-side event set cross-device. Same
  // offline-preferred / forceRemote contract as WSL/FSL. Outbox is
  // reported through the production sync-status pipeline via the shared
  // entity-mutation reconciler.
  const refreshCurrentHarvestLogs = useCallback(async (options?: {
    preferLocal?: boolean
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!productionContext) {
      setHarvestLogs([])
      return false
    }

    try {
      const logs = await harvestLogApi.list({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId
      }, {
        preferLocal: options?.preferLocal,
        forceRemote: options?.forceRemote
      })
      setHarvestLogs(logs)
      return true
    } catch {
      if (!options?.preferLocal) {
        setHarvestLogs([])
      }
      return false
    }
  }, [productionContext])

  // FCR-in-flight PRD (2026-09-11): mirror `refreshCurrentHarvestLogs` for
  // FeedLog — read-only input for the in-flight FCR card's feed-kg-to-date
  // numerator. Biomass has no FeedLog write path.
  const refreshCurrentFeedLogs = useCallback(async (options?: {
    preferLocal?: boolean
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!productionContext) {
      setFeedLogs([])
      return false
    }

    try {
      const logs = await feedLogApi.list({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId
      }, {
        preferLocal: options?.preferLocal,
        forceRemote: options?.forceRemote
      })
      setFeedLogs(logs)
      return true
    } catch {
      if (!options?.preferLocal) {
        setFeedLogs([])
      }
      return false
    }
  }, [productionContext])

  const refreshCurrentProductionSyncStatus = useCallback(async (): Promise<EntityBatchSyncStatus | null> => {
    const rowIds = Array.from(new Set(productionRows.map((row) => row.id).filter(Boolean)))
    const mortalityLogIds = Array.from(new Set(storedMortalityLogs.map((log) => log.id).filter(Boolean)))

    if (rowIds.length === 0 && mortalityLogIds.length === 0) {
      const emptyStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setProductionSyncStatus(emptyStatus)
      return emptyStatus
    }

    try {
      const [productionStatus, mortalityStatus] = await Promise.all([
        rowIds.length > 0
          ? readProductionUnitTableRowSyncStatus(rowIds)
          : Promise.resolve({
            syncState: 'synced',
            blockedCount: 0,
            errorMessage: null,
            blockers: []
          } as EntityBatchSyncStatus),
        mortalityLogIds.length > 0
          ? mortalityLogApi.readMortalityLogSyncStatus(mortalityLogIds)
          : Promise.resolve({
            syncState: 'synced',
            blockedCount: 0,
            errorMessage: null,
            blockers: []
          } as EntityBatchSyncStatus)
      ])

      const status = mergeBatchSyncStatuses([productionStatus, mortalityStatus])
      if (!status) {
        setProductionSyncStatus(null)
        return null
      }
      setProductionSyncStatus(status)
      return status
    } catch {
      setProductionSyncStatus(null)
      return null
    }
  }, [storedMortalityLogs, productionRows])

  // v2.1 finding #61: keep the post-drain refresh ref pointed at the
  // latest `refreshCurrentProductionSyncStatus` identity so
  // `applyProductionTableStatus` (declared earlier in the file) can call
  // through it from its `syncPromise.then` callback. See the ref's
  // declaration block above for the full rationale.
  useEffect(() => {
    refreshProductionSyncStatusFnRef.current = refreshCurrentProductionSyncStatus
  }, [refreshCurrentProductionSyncStatus])

  const retryPendingProductionTableSync = useCallback(async (): Promise<void> => {
    const action = pendingProductionActionRef.current
    const rowIds = Array.from(pendingProductionRowIdsRef.current)

    if (!productionContext || !action || rowIds.length === 0) return

    const nextState = await retryProductionUnitTableRowSync(rowIds)
    if (nextState === 'synced') {
      pendingProductionRowIdsRef.current = new Set()
      pendingProductionActionRef.current = null
    }

    setProductionTableStatusMessage(t(getProductionTableStatusKey(action, nextState)))
    await refreshCurrentProductionRows({ forceRemote: true })
    await refreshCurrentProductionSyncStatus()
  }, [getProductionTableStatusKey, productionContext, refreshCurrentProductionRows, refreshCurrentProductionSyncStatus, t])

  const refreshBiomassLogs = useCallback(async (): Promise<void> => {
    if (!productionContext) return

    const nextVersion = productionTableStatusVersionRef.current + 1
    productionTableStatusVersionRef.current = nextVersion
    setProductionTableRefreshing(true)
    setProductionTableStatusMessage(t('biomass.status.refreshing'))

    const action = pendingProductionActionRef.current
    const rowIds = Array.from(pendingProductionRowIdsRef.current)
    const nextState = action && rowIds.length > 0
      ? await retryProductionUnitTableRowSync(rowIds)
      : null

    if (nextState === 'synced') {
      pendingProductionRowIdsRef.current = new Set()
      pendingProductionActionRef.current = null
    }

    const [productionRefreshed, mortalityRefreshed] = await Promise.all([
      refreshCurrentProductionRows({ forceRemote: true }),
      refreshCurrentMortalityLogs({ forceRemote: true })
    ])
    const refreshed = productionRefreshed && mortalityRefreshed
    if (productionTableStatusVersionRef.current !== nextVersion) {
      setProductionTableRefreshing(false)
      return
    }

    if (!refreshed) {
      if (action && nextState && nextState !== 'synced') {
        setProductionTableStatusMessage(t(getProductionTableStatusKey(action, nextState)))
      } else {
        setProductionTableStatusMessage(t('biomass.status.refreshFailed'))
      }
      setProductionTableRefreshing(false)
      return
    }

    const nextSyncStatus = await refreshCurrentProductionSyncStatus()
    if (nextSyncStatus) {
      const outcome = deriveBiomassSyncOutcome({
        status: nextSyncStatus,
        skippedNotFoundCount: 0
      })
      setProductionSyncLastOutcome({
        ...outcome,
        attemptedAtIso: new Date().toISOString()
      })
    }

    if (action && nextState) {
      setProductionTableStatusMessage(t(getProductionTableStatusKey(action, nextState)))
    } else {
      setProductionTableStatusMessage(t('biomass.status.refreshComplete'))
    }

    setProductionTableRefreshing(false)
  }, [getProductionTableStatusKey, productionContext, refreshCurrentMortalityLogs, refreshCurrentProductionRows, refreshCurrentProductionSyncStatus, t])

  useEffect(() => {
    clearProductionTableStatus()
    setProductionTableRefreshing(false)
    setProductionSyncStatus(null)
    setProductionSyncLastOutcome(null)
    setRetryingProductionSync(false)
  }, [clearProductionTableStatus, farmId, productionUnitId])

  useEffect(() => {
    if (!productionContext) return
    let cancelled = false
    const canRefresh = Boolean(productionContext.farmId) && Boolean(productionContext.productionUnitId)
    if (canRefresh) {
      void refreshCurrentProductionRows()
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
      void refreshCurrentMortalityLogs({ preferLocal: true })
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
      void refreshCurrentWeightSampleLogs({ preferLocal: true })
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
      void refreshCurrentFishStockLogs({ preferLocal: true })
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
      void refreshCurrentHarvestLogs({ preferLocal: true })
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
      void refreshCurrentFeedLogs({ preferLocal: true })
        .then(() => {
          if (cancelled) return
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
    }
  }, [productionContext, refreshCurrentMortalityLogs, refreshCurrentProductionRows, refreshCurrentWeightSampleLogs, refreshCurrentFishStockLogs, refreshCurrentHarvestLogs, refreshCurrentFeedLogs])

  useEffect(() => {
    if (!productionContext) {
      setProductionSyncStatus(null)
      return
    }
    void refreshCurrentProductionSyncStatus()
  }, [productionContext, productionRows, refreshCurrentProductionSyncStatus])

  useEffect(() => {
    const onOnline = () => {
      void retryPendingProductionTableSync()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void retryPendingProductionTableSync()
    }

    const onFocus = () => {
      void retryPendingProductionTableSync()
    }

    globalThis.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    globalThis.addEventListener('focus', onFocus)

    return () => {
      globalThis.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      globalThis.removeEventListener('focus', onFocus)
    }
  }, [retryPendingProductionTableSync])

  useEffect(() => {
    if (typeof globalThis.window === 'undefined') return

    const drainIfWarranted = () => {
      const productionRowIds = Array.from(new Set(productionRows.map((row) => row.id).filter(Boolean)))
      const mortalityLogIds = Array.from(new Set(storedMortalityLogs.map((log) => log.id).filter(Boolean)))
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine
      const plan = selectBiomassReconnectDrainTargets({
        status: productionSyncStatus,
        isOnline,
        productionRowIds,
        mortalityLogIds
      })
      if (!plan.shouldDrain) return

      void (async () => {
        try {
          await Promise.all([
            plan.productionRowIds.length > 0
              ? retryProductionUnitTableRowSyncStatus(plan.productionRowIds)
              : Promise.resolve(null),
            plan.mortalityLogIds.length > 0
              ? mortalityLogApi.retryMortalityLogSyncStatusWithReport(plan.mortalityLogIds)
              : Promise.resolve(null)
          ])

          await refreshCurrentProductionSyncStatus()
        } catch {
          // Reconnect-driven drains are best-effort; the explicit Retry sync control
          // remains available with full outcome messaging. Intentionally do NOT touch
          // productionSyncLastOutcome here so the "Last retry result" line stays
          // reserved for explicit user clicks.
        }
      })()
    }

    const onOnline = () => drainIfWarranted()
    const onFocus = () => drainIfWarranted()
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      drainIfWarranted()
    }

    globalThis.addEventListener('online', onOnline)
    globalThis.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      globalThis.removeEventListener('online', onOnline)
      globalThis.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [productionSyncStatus, productionRows, storedMortalityLogs, refreshCurrentProductionSyncStatus])

  useEffect(() => {
    if (!productionContext) {
      setProductionRows([])
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const rows = await listProductionUnitTableRows(productionContext)
        if (!cancelled) setProductionRows(rows)
      } catch {
        if (!cancelled) setProductionRows([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [productionContext, dataRevision])

  useEffect(() => {
    if (!productionContext) {
      setMortalityLogs([])
      setWeightSampleLogs([])
      setFishStockLogs([])
      setHarvestLogs([])
    }
  }, [productionContext])

  useEffect(() => {
    if (!productionContext) {
      setProjectionWaterRows([])
      setMortalityMatrixData(getDefaultMatrixForSpecies(loadStoredSpecies()))
      return
    }

    let cancelled = false
    const selectedFarm = resolveSelectedFarm(availableFarms as FarmLike[], productionContext.farmId)
    const nextSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm) ?? loadStoredSpecies()
    const waterRowsPromise: Promise<WaterDataRow[]> = refreshWaterTableRowsFromApi(productionContext)
      .catch(() => loadWaterTableRows(productionContext))

    void (async () => {
      const [waterLogsResult, mortalityMatrixResult] = await Promise.allSettled([
        waterRowsPromise,
        loadMortalityMatrix(nextSpeciesKey, { preferRecentLocal: true })
      ])

      if (cancelled) return

      if (waterLogsResult.status === 'fulfilled') {
        setProjectionWaterRows(normalizeProjectionWaterLogs(
          waterLogsResult.value.map((row) => ({
            recordedAt: row.date,
            lot: row.lot,
            unmappedLot: row.unmappedLot,
            temperatureC: row.temperature
          }))
        ))
      } else {
        console.warn('Biomass projection: failed to load water logs', waterLogsResult.reason)
        setProjectionWaterRows([])
      }

      if (mortalityMatrixResult.status === 'fulfilled') {
        setMortalityMatrixData(mortalityMatrixResult.value.matrix)
      } else {
        console.warn('Biomass projection: failed to load mortality matrix', mortalityMatrixResult.reason)
        setMortalityMatrixData(getDefaultMatrixForSpecies(nextSpeciesKey))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [availableFarms, productionContext])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setBiomassKPIs([])
      setHistoricalBiomassKPIs([])
      return
    }

    const selectedFarm = resolveSelectedFarm(availableFarms as FarmLike[], farmId)
    const projectionSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm) ?? loadStoredSpecies()

    const { active, historical } = computeBiomassKpis({
      farms: availableFarms,
      farmId,
      productionUnitId,
      productionRows: [...samplingRows, ...crossFarmSupplementOverlaid],
      availableLots,
      waterRows: projectionWaterRows,
      mortalityMatrix: mortalityMatrixData,
      speciesKey: projectionSpeciesKey,
      lineageRows: lotLineageRows,
      feedLogs,
      harvestLogs,
      stockedBiomassByLotId: new Map(Array.from(
        buildLotFcrInputs({ weightSampleLogs, mortalityLogs, fishStockLogs }),
        ([lotId, inputs]) => [lotId, inputs.stockedBiomassKg] as const
      ))
    })
    setBiomassKPIs(active)
    setHistoricalBiomassKPIs(historical)
  }, [farmId, productionUnitId, availableFarms, samplingRows, crossFarmSupplementOverlaid, availableLots, lotLineageRows, projectionWaterRows, mortalityMatrixData, feedLogs, harvestLogs, weightSampleLogs, mortalityLogs, fishStockLogs])

  const biomassSummary = useMemo(() => {
    // v2.1 finding #11: KPI tiles must use one Active KPI per lot, not one per
    // ProductionRecord row. With finding #4's same-day row redundancy still in
    // place, a lot with N Sampling rows on a single day would otherwise inflate
    // `Total Fish stock` / `Total Active Biomass` / `Active lots` by a factor
    // of N. `biomassKPIs` / `historicalBiomassKPIs` are already sorted
    // newest-first by `sortBiomassKpisChronologically(...).reverse()`, so
    // picking the first occurrence per `lotToken` yields the most recent
    // snapshot. See `summarizeActiveAndHistoricalBiomassKpis` for the U2
    // Activo/Historico bucketing rule.
    const {
      activeLots: lastWeighedActiveLots,
      historicalLots,
      totalHistoricalBiomass
    } = summarizeActiveAndHistoricalBiomassKpis(biomassKPIs, historicalBiomassKPIs)
    // Findings #198: stock, biomass and average weight come from each active
    // lot's latest stock with its last known weight, not its last weighed day.
    const { activeLots, totalActiveStock, totalActiveBiomass, averageWeight } = applyCurrentLotState({
      activeLots: lastWeighedActiveLots,
      roster: availableLots,
      currentByLotId: computeCurrentBiomassPerLot({
        productionRows: samplingRows,
        mortalityLogs,
        weightSampleLogs,
        fishStockLogs
      })
    })

    let observedMortalitySum = 0
    let observedLotsCount = 0
    let survivalSum = 0
    let survivalLotsCount = 0
    // Feed the same deduped Active set into the projection summary so the
    // projected-biomass tiles match `Total Active Biomass` (1 contribution per lot).
    const projectionScenarioSummary = deriveBiomassProjectionScenarioSummary(lastWeighedActiveLots)

    lastWeighedActiveLots.forEach((kpi) => {
      if (kpi.observedMortalityRate !== null) {
        observedMortalitySum += kpi.observedMortalityRate
        observedLotsCount += 1
      }
      if (kpi.survivalRate !== null) {
        survivalSum += kpi.survivalRate
        survivalLotsCount += 1
      }
    })

    const averageObservedMortalityRate = observedLotsCount > 0 ? observedMortalitySum / observedLotsCount : null
    const averageSurvivalRate = survivalLotsCount > 0 ? survivalSum / survivalLotsCount : null
    const {
      averageExpectedMortalityRate,
      projectedLotsCount,
      projectedTrendLotsCount,
      totalProjectedBiomass,
      totalProjectedBiomassCurrentTrend
    } = projectionScenarioSummary

    return {
      activeLots,
      historicalLots,
      totalActiveBiomass,
      totalHistoricalBiomass,
      totalActiveStock,
      averageWeight,
      averageObservedMortalityRate,
      observedLotsCount,
      averageSurvivalRate,
      survivalLotsCount,
      totalProjectedBiomass,
      totalProjectedBiomassCurrentTrend,
      averageExpectedMortalityRate,
      projectedLotsCount,
      projectedTrendLotsCount
    }
  }, [biomassKPIs, historicalBiomassKPIs, availableLots, samplingRows, mortalityLogs, weightSampleLogs, fishStockLogs])

  const selectedFarm = useMemo<FarmLike | undefined>(() => {
    const token = String(farmId)
    if (!token) return undefined
    const farms = availableFarms as FarmLike[]
    return farms.find((farm) => {
      const id = asStableIdString(farm?.id)
      if (id && id === token) return true
      const name = farm?.name
      return typeof name === 'string' && name === token
    })
  }, [availableFarms, farmId])

  const speciesKey = useMemo<ProductionSpeciesKey>(() => {
    const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
    return farmSpeciesKey ?? loadStoredSpecies()
  }, [selectedFarm])
  const stockLabel = speciesKey === 'shrimp' ? t('biomass.stockLabel.shrimp') : t('biomass.stockLabel.fish')
  const stockEmoji = speciesKey === 'shrimp' ? '🦐' : '🐟'

  const formatStageLabel = (stage: string) => formatStageLabelWithT(t, stage)
  const formatMethodLabel = (method: string) => formatMethodLabelWithT(t, method)

  const selectedProductionUnit = useMemo(
    () => resolveSelectedProductionUnit(selectedFarm, productionUnitId),
    [selectedFarm, productionUnitId]
  )

  const selectedFarmLabel = resolveFarmLabel(selectedFarm, farmId)
  const selectedProductionUnitLabel = resolveProductionUnitLabel(selectedProductionUnit, productionUnitId)

  const {
    activeLots,
    historicalLots,
    totalActiveBiomass,
    totalHistoricalBiomass,
    totalActiveStock,
    averageWeight,
    averageObservedMortalityRate,
    observedLotsCount,
    averageSurvivalRate,
    survivalLotsCount,
    totalProjectedBiomass,
    averageExpectedMortalityRate,
    projectedLotsCount
  } = biomassSummary

  const performanceSnapshot = useMemo(
    () => deriveMortalityPerformanceSnapshot(biomassKPIs),
    [biomassKPIs]
  )

  // "Recent" = newest recordDate first. `samplingRows` derives from
  // `productionRows`, which `DexieProductionTableRepo.listByContext` returns
  // sorted by `date` descending (the API sorts `recordDate: 'desc'` to match);
  // a fresh local save is prepended and re-sorts into date order on the next
  // context refresh. The recent-log panels render the 10 newest by default and
  // reveal older rows via "Load more" — see `RECENT_LOG_LIST_INITIAL_VISIBLE`.
  const recentRows = useMemo(() => samplingRows.slice(), [samplingRows])

  // Mirror the Database screen's display-only derivation: when a sampling row's
  // fishStock is blank but previous known stock and same-day mortality (after
  // MORT-12 overlay) are available, surface S_t = S_{t-1} - M_t as a derived
  // value. `samplingRows` is already overlaid, so we feed it directly.
  const displayedEndOfDayStockByRowId = useMemo(
    () => computeDisplayedEndOfDayStockByRowId(samplingRows),
    [samplingRows]
  )

  // v2.1 finding #49: delegate to the shared helper so the sentinel
  // `YYYY-MM-DDT00:00:00Z` (the "Add Row" default + CSV date-only imports)
  // renders on the same local calendar day as the Database screen instead of
  // TZ-shifting to the previous day's evening in negative-UTC timezones.
  const formatRowDateTime = useCallback((raw: string) => {
    const formatted = formatStoredDateTimeAsLocal(raw)
    return formatted || raw
  }, [])

  const recentSamplingRows = useMemo(() => {
    // Sampling rows can be weight-only, stock-only, mortality-only, or any combination;
    // a count row shows here too when it also has weight or mortality (findings #193).
    return recentRows.filter((row) => belongsInSamplingList(row))
  }, [recentRows])

  const recentCountRows = useMemo(() => {
    return recentRows.filter((row) => hasStockSignal(row))
  }, [recentRows])

  // Findings #186: each count row's FishStockLog, for "Corregir…" and its "Corregido" badge.
  const countLogIdByRowId = useMemo(() => {
    const out = new Map<string, string>()
    for (const row of recentCountRows) {
      const log = findLinkedFishStockLog(row, fishStockLogs)
      if (log) out.set(String(row.id), log.id)
    }
    return out
  }, [recentCountRows, fishStockLogs])
  const [countCorrectionKey, setCountCorrectionKey] = useState(0)
  const countCorrectionCounts = useCorrectionCounts('fish-stock-log', Array.from(countLogIdByRowId.values()), countCorrectionKey)

  const recentHarvestRows = useMemo(() => {
    return recentRows.filter((row) => Boolean((row as any).harvestKg?.trim?.() || (row as any).harvestCount?.trim?.()))
  }, [recentRows])

  // v2.1 finding #35 → v2.1 finding #60 (WSL-06): badge count now derives
  // from the server-backed `WeightSampleLog` set via the WSL-03 rollup
  // helper, matching the overlay's authority contract. Pre-WSL-04 rehearsal
  // rows that only exist in the sidecar no longer surface the badge — that
  // data is explicitly discarded per the WSL cluster's rollout note.
  const samplingEventCountByRowId = useMemo(() => {
    const counts: Record<string, number> = {}
    const rollup = computeWeightSampleRollupByRowId(productionRows, weightSampleLogs)
    for (const [rowId, entry] of rollup) {
      counts[rowId] = entry.eventCount
    }
    return counts
  }, [productionRows, weightSampleLogs])

  const lotSelectPlaceholder = getLotSelectPlaceholder({
    productionUnitId,
    lotsLoading,
    lotsCount: interactiveLots.length,
    t
  })

  const productionSyncAttentionMessage = useMemo(() => {
    if (!productionSyncStatus) return null
    if (productionSyncStatus.blockedCount > 0) {
      if (productionSyncStatus.errorMessage) {
        return t('biomass.sync.attentionBlockedWithReason', {
          count: productionSyncStatus.blockedCount,
          reason: productionSyncStatus.errorMessage
        })
      }

      return t('biomass.sync.attentionBlocked', { count: productionSyncStatus.blockedCount })
    }

    if (productionSyncStatus.syncState === 'offline') {
      return t('biomass.sync.attentionOffline')
    }

    if (productionSyncStatus.syncState === 'pending') {
      return t('biomass.sync.attentionPending')
    }

    return null
  }, [productionSyncStatus, t])

  const productionSyncLastResultSummary = useMemo(() => {
    if (!productionSyncLastOutcome) return null

    return {
      resultLabel: t(productionSyncLastOutcome.resultKey, productionSyncLastOutcome.resultParams),
      attemptedAtLabel: formatRowDateTime(productionSyncLastOutcome.attemptedAtIso)
    }
  }, [formatRowDateTime, productionSyncLastOutcome, t])

  const handleRetryProductionSync = useCallback(async (): Promise<void> => {
    if (!productionContext) return

    const rowIds = Array.from(new Set(productionRows.map((row) => row.id).filter(Boolean)))
    const mortalityLogIds = Array.from(new Set(storedMortalityLogs.map((log) => log.id).filter(Boolean)))

    if (rowIds.length === 0 && mortalityLogIds.length === 0) {
      const syncedStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setProductionSyncStatus(syncedStatus)
      const outcome = deriveBiomassSyncOutcome({ status: syncedStatus, skippedNotFoundCount: 0 })
      setProductionSyncLastOutcome({ ...outcome, attemptedAtIso: new Date().toISOString() })
      return
    }

    setRetryingProductionSync(true)
    setProductionTableStatusMessage(t('biomass.sync.retrying'))

    try {
      const [productionRetryStatus, mortalityRetryReport] = await Promise.all([
        rowIds.length > 0
          ? retryProductionUnitTableRowSyncStatus(rowIds)
          : Promise.resolve({
            syncState: 'synced',
            blockedCount: 0,
            errorMessage: null,
            blockers: []
          } as EntityBatchSyncStatus),
        mortalityLogIds.length > 0
          ? mortalityLogApi.retryMortalityLogSyncStatusWithReport(mortalityLogIds)
          : Promise.resolve({
            status: {
              syncState: 'synced',
              blockedCount: 0,
              errorMessage: null,
              blockers: []
            } as EntityBatchSyncStatus,
            skippedNotFoundCount: 0
          })
      ])

      const nextStatus = mergeBatchSyncStatuses([productionRetryStatus, mortalityRetryReport.status])
      if (!nextStatus) {
        setProductionSyncStatus(null)
        return
      }
      setProductionSyncStatus(nextStatus)

      const outcome = deriveBiomassSyncOutcome({
        status: nextStatus,
        skippedNotFoundCount: mortalityRetryReport.skippedNotFoundCount
      })
      setProductionSyncLastOutcome({
        ...outcome,
        attemptedAtIso: new Date().toISOString()
      })
      setProductionTableStatusMessage(t(outcome.resultKey, outcome.resultParams))

      await Promise.all([
        refreshCurrentProductionRows({ forceRemote: true }),
        refreshCurrentMortalityLogs({ forceRemote: true })
      ])
    } catch {
      setProductionTableStatusMessage(t('biomass.status.refreshFailed'))
    } finally {
      setRetryingProductionSync(false)
    }
  }, [storedMortalityLogs, productionContext, productionRows, refreshCurrentMortalityLogs, refreshCurrentProductionRows, t])

  const analyzeBody = (
    <BiomassAnalyzeBody
      t={t}
      biomassKPIs={biomassKPIs}
      performanceSnapshot={performanceSnapshot}
      viewMode={viewMode}
      isSmallScreen={isSmallScreen}
      stockLabel={stockLabel}
      totalActiveBiomass={totalActiveBiomass}
      totalActiveStock={totalActiveStock}
      averageWeight={averageWeight}
      averageObservedMortalityRate={averageObservedMortalityRate}
      observedLotsCount={observedLotsCount}
      averageSurvivalRate={averageSurvivalRate}
      survivalLotsCount={survivalLotsCount}
      totalProjectedBiomass={totalProjectedBiomass}
      totalProjectedBiomassCurrentTrend={biomassSummary.totalProjectedBiomassCurrentTrend}
      averageExpectedMortalityRate={averageExpectedMortalityRate}
      projectedLotsCount={projectedLotsCount}
      projectedTrendLotsCount={biomassSummary.projectedTrendLotsCount}
      activeLotsCount={activeLots.length}
      activeLots={activeLots}
      historicalLots={historicalLots}
      totalHistoricalBiomass={totalHistoricalBiomass}
      selectedFarmLabel={selectedFarmLabel}
      selectedProductionUnitLabel={selectedProductionUnitLabel}
      formatStageLabel={formatStageLabel}
    />
  )

  const resetLogDraft = () => {
    setLogDraft({
      recordedAt: new Date().toISOString(),
      lotId: '',
      averageWeightG: '',
      dailyMortality: '',
      notes: ''
    })
    setLogError(null)
  }

  const resetCountDraft = () => {
    setCountDraft({
      recordedAt: new Date().toISOString(),
      lotId: '',
      method: 'estimate',
      stockCount: '',
      notes: ''
    })
    setCountError(null)
  }

  const resetHarvestDraft = () => {
    setHarvestDraft({
      recordedAt: new Date().toISOString(),
      lotId: '',
      harvestKg: '',
      harvestCount: '',
      notes: ''
    })
    setHarvestError(null)
  }

  const applyRowToDraft = (row: BiomassDataRow) => {
    const resolvedLotId = resolveBiomassDraftLotId(row, availableLots)

    setLogDraft({
      recordedAt: row.date,
      lotId: resolvedLotId,
      averageWeightG: row.weight ?? '',
      dailyMortality: (row as any).dailyMortality ?? '',
      notes: (row as any).upVol ?? ''
    })
    setLogError(null)
  }

  const saveLogDraft = async () => {
    if (!canEditBiomassLogs) return
    if (!productionContext) return

    const lot = resolveBiomassDraftLot(logDraft.lotId, interactiveLots)
    if (!lot) {
      setLogError(t('biomass.error.chooseLot'))
      return
    }

    // v2.1 finding #12: Sampling is the operational-entry surface; the
    // Database table and "Recent sampling rows" cards are summary surfaces and
    // must show one row per (lot, calendar day). The MortalityLog write
    // remains 1-per-event (raw operational log), but the ProductionRecord
    // upsert reuses any existing row for the same (lotId, local day) so a
    // second/third sampling on the same day updates the row in place instead
    // of stacking visible duplicates. MORT-12 still derives the day's total
    // from the underlying events, and Datatable.deleteRowById (finding #8)
    // already removes the linked log when a row is deleted.
    const recordedDayKey = toLocalCalendarDayKey(logDraft.recordedAt)
    const existingSameDayRow = recordedDayKey
      ? productionRows.find((row) => {
          if (!row || typeof row.id !== 'string' || !row.id) return false
          if (String(row.lotId ?? '') !== lot.id) return false
          return toLocalCalendarDayKey(String(row.date ?? '')) === recordedDayKey
        })
      : undefined
    const id = existingSameDayRow ? String(existingSameDayRow.id) : createId('prod')

    const validatedSamplingDraft = validateSamplingDraft({
      averageWeightG: logDraft.averageWeightG,
      dailyMortality: logDraft.dailyMortality
    }, t)
    if (!validatedSamplingDraft.ok) {
      setLogError(validatedSamplingDraft.error)
      return
    }

    const normalizedAverageWeightG = validatedSamplingDraft.value.normalizedAverageWeightG
    const normalizedDailyMortality = validatedSamplingDraft.value.normalizedDailyMortality

    // T3-C-01 (2026-07-13): fish-stock input removed from the Muestreo /
    // Sampling panel. Operators now record fish-stock counts in the
    // Conteo/Estimación panel via `saveCountDraft`, which writes a
    // `FishStockLog` (T3-A-03 bridge). See
    // docs/mvp-2-2-architecture-cleanup-plan.md T3-C-01.

    // v2.1 finding #35 → v2.1 finding #60 (WSL-06 write cutover): per-event
    // weight evidence now lives in the server-backed `WeightSampleLog` table.
    // The stored `ProductionRecord.weightGrams` becomes the last-write value;
    // the true same-day mean is derived at render time by
    // `overlayWeightSamplesOnProductionRows`. Cross-device correctness comes
    // from the WSL-02 entity outbox verb; MORT-12 mortality treatment is
    // unchanged.
    const incomingWeightG = Number(normalizedAverageWeightG)

    const draft: BiomassLogDraft = {
      id,
      recordedAt: logDraft.recordedAt,
      lotId: lot.id,
      lotKey: lot.lotKey || `Lot ${lot.id}`,
      // T3-D-04a: row-level `stage` is now a snapshot of `Lot.stage` at
      // write time, not an operator-editable field. Non-Active LotStage
      // values (Inactive/Harvested/Planned/Dormant) collapse to 'Inactive'.
      stage: toProductionRowStage(availableLots.find((candidate) => candidate.id === lot.id)?.stage),
      averageWeightG: normalizedAverageWeightG,
      dailyMortality: normalizedDailyMortality,
      notes: String(logDraft.notes ?? '')
    }

    setLogSaving(true)
    setLogError(null)
    try {
      // T3-A-07b (2026-07-06): the row seed no longer carries biology cells;
      // `buildProductionRowFromBiomassLogDraft` emits empty strings for
      // `weight` / `fishStock` / `dailyMortality`. The overlay chain
      // populates those cells at render time from the MortalityLog and
      // WeightSampleLog events written below.
      const nextRow = buildProductionRowFromBiomassLogDraft(draft)
      const productionResult = await upsertProductionUnitTableRowAndSync(productionContext, nextRow)
      let mortalityResult: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'> | null = null
      let nextMortalityLog: MortalityLog | null = null

      if (normalizedDailyMortality) {
        const createdMortality = await mortalityLogApi.create({
          farmId: productionContext.farmId,
          productionUnitId: productionContext.productionUnitId,
          lot: lot.lotKey || `Lot ${lot.id}`,
          lotId: lot.id,
          recordedAt: draft.recordedAt,
          mortalityCount: normalizedDailyMortality,
          notes: draft.notes,
          metadata: buildBiomassSamplingMortalityMetadata({
            productionRowId: nextRow.id,
            lotId: lot.id,
            stage: draft.stage
          })
        })
        nextMortalityLog = createdMortality.data
        mortalityResult = createdMortality
      }

      // v2.1 finding #60 (WSL-04/06): mirror the MortalityLog write for weight
      // evidence so per-event Sampling weights become server-backed and
      // visible cross-device. Optimistically append to `weightSampleLogs`
      // state so the overlay + `(averaged from N events)` badge reflect the
      // new event before the next remote list refresh. See
      // [docs/weight-sample-log-implementation-plan.md](../../docs/weight-sample-log-implementation-plan.md).
      let weightSampleResult: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'> | null = null
      let nextWeightSampleLog: WeightSampleLog | null = null
      if (Number.isFinite(incomingWeightG) && incomingWeightG > 0) {
        const createdWeightSample = await weightSampleLogApi.create({
          farmId: productionContext.farmId,
          productionUnitId: productionContext.productionUnitId,
          lot: lot.lotKey || `Lot ${lot.id}`,
          lotId: lot.id,
          productionRowId: nextRow.id,
          recordedAt: draft.recordedAt,
          weightGrams: String(incomingWeightG),
          notes: draft.notes,
          metadata: buildBiomassSamplingWeightMetadata({
            productionRowId: nextRow.id,
            lotId: lot.id,
            stage: draft.stage
          })
        })
        nextWeightSampleLog = createdWeightSample.data
        weightSampleResult = createdWeightSample
      }

      setProductionRows((prev) => {
        if (existingSameDayRow) {
          // Replace in place to preserve list order; the upsert above already
          // overwrote weight / stage / notes with the latest sampling values.
          return prev.map((row) => (String(row?.id ?? '') === id ? nextRow : row))
        }
        return [nextRow, ...prev]
      })
      if (nextMortalityLog) {
        setMortalityLogs((prev) => [nextMortalityLog, ...prev])
      }
      if (nextWeightSampleLog) {
        setWeightSampleLogs((prev) => [nextWeightSampleLog, ...prev])
      }

      applyProductionTableStatus('saved', [nextRow.id], combineEntityMutationResults([
        productionResult,
        mortalityResult,
        weightSampleResult
      ]))
      resetLogDraft()
    } catch (error) {
      console.error('Biomass log save failed', error)
      setLogError(t('biomass.error.saveFailed'))
    } finally {
      setLogSaving(false)
    }
  }

  const saveCountDraft = async () => {
    if (!canEditBiomassLogs) return
    if (!productionContext) return

    const lot = resolveBiomassDraftLot(countDraft.lotId, interactiveLots)
    if (!lot) {
      setCountError(t('biomass.error.chooseLot'))
      return
    }

    const stockCount = String(countDraft.stockCount ?? '').trim()
    if (!stockCount) {
      setCountError(t('biomass.error.enterRequired', { field: stockLabel }))
      return
    }

    const normalizedStock = normalizeWholeNumberField(stockCount, stockLabel, t)
    if (!normalizedStock.ok) {
      setCountError(normalizedStock.error)
      return
    }

    const stockValue = Number(normalizedStock.normalized)
    if (Number.isFinite(stockValue) && stockValue > 10_000_000) {
      setCountError(t('datatable.numericValidation.max', { label: stockLabel, max: 10_000_000 }))
      return
    }

    // Keep one ProductionRecord row per (lot, calendar day) on the Biomass
    // write surface. Count events remain 1-per-event in FishStockLog, but the
    // summary row should update in place to avoid duplicate rows in Database.
    const recordedDayKey = toLocalCalendarDayKey(countDraft.recordedAt)
    const existingSameDayRow = recordedDayKey
      ? productionRows.find((row) => {
          if (!row || typeof row.id !== 'string' || !row.id) return false
          if (String(row.lotId ?? '') !== lot.id) return false
          return toLocalCalendarDayKey(String(row.date ?? '')) === recordedDayKey
        })
      : undefined
    const id = existingSameDayRow ? String(existingSameDayRow.id) : createId('prod')
    // T3-D-04a: row-level `stage` snapshot derived from `Lot.stage`, not
    // from an operator-picked dropdown. Non-Active LotStage values
    // (Inactive/Harvested/Planned/Dormant) collapse to 'Inactive'.
    const derivedStage = toProductionRowStage(availableLots.find((candidate) => candidate.id === lot.id)?.stage)
    const nextRow: BiomassDataRow = existingSameDayRow
      ? {
          ...existingSameDayRow,
          id,
          date: countDraft.recordedAt,
          lotId: lot.id,
          lot: lot.lotKey || `Lot ${lot.id}`,
          stage: derivedStage,
          fishStock: normalizedStock.normalized,
          upVol: String(countDraft.notes ?? ''),
          stockMethod: countDraft.method
        }
      : {
          id,
          date: countDraft.recordedAt,
          lotId: lot.id,
          lot: lot.lotKey || `Lot ${lot.id}`,
          stage: derivedStage,
          weight: '',
          dailyMortality: '',
          fishStock: normalizedStock.normalized,
          upVol: String(countDraft.notes ?? ''),
          health: '',
          healthDetail: '',
          stockMethod: countDraft.method
        }

    setCountSaving(true)
    setCountError(null)
    try {
      const productionResult = await upsertProductionUnitTableRowAndSync(productionContext, nextRow)

      // v2.1 finding: same-shape as WSL/MortalityLog write parity in
      // `saveLogDraft`. Post-T3-A-07b the `ProductionRecord.fishStock`
      // cache column is retired, so a Conteo/Estimación save that only
      // touches the row cell vanishes on the next `refreshProductionUnitTableFromApi`.
      // The authoritative value must live in a `FishStockLog` event
      // consumed by `overlayFishStockOnProductionRows`. Method mapping:
      // countDraft.method 'exact' → 'count', 'estimate' → 'estimation'
      // (client draft vocabulary vs FishStockLog canonical vocabulary).
      const fishStockCreated = await fishStockLogApi.create({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId,
        lot: lot.lotKey || `Lot ${lot.id}`,
        lotId: lot.id,
        productionRowId: id,
        recordedAt: countDraft.recordedAt,
        countedStock: stockValue,
        method: countDraft.method === 'exact' ? 'count' : 'estimation',
        notes: countDraft.notes ? String(countDraft.notes) : undefined,
        metadata: buildBiomassFishStockMetadata({
          productionRowId: id,
          lotId: lot.id,
          stage: derivedStage
        })
      })
      const nextFishStockLog = fishStockCreated.data

      setProductionRows((prev) => {
        if (existingSameDayRow) {
          return prev.map((row) => (String(row?.id ?? '') === id ? nextRow : row))
        }
        return [nextRow, ...prev]
      })
      setFishStockLogs((prev) => [nextFishStockLog, ...prev])
      applyProductionTableStatus('saved', [nextRow.id], combineEntityMutationResults([
        productionResult,
        fishStockCreated
      ]))
      resetCountDraft()
    } catch (error) {
      console.error('Count/estimate save failed', error)
      setCountError(t('biomass.error.saveFailed'))
    } finally {
      setCountSaving(false)
    }
  }

  const saveHarvestDraft = async () => {
    if (!canEditBiomassLogs) return
    if (!productionContext) return

    const lot = resolveBiomassDraftLot(harvestDraft.lotId, interactiveLots)
    if (!lot) {
      setHarvestError(t('biomass.error.chooseLot'))
      return
    }

    const harvestKg = String(harvestDraft.harvestKg ?? '').trim()
    const harvestCount = String(harvestDraft.harvestCount ?? '').trim()
    if (!harvestKg && !harvestCount) {
      setHarvestError(t('biomass.error.harvestRequired'))
      return
    }

    const id = createId('prod')
    // T3-D-04a: row-level `stage` snapshot derived from `Lot.stage`, not
    // from an operator-picked dropdown. Non-Active LotStage values
    // (Inactive/Harvested/Planned/Dormant) collapse to 'Inactive'.
    const derivedStage = toProductionRowStage(availableLots.find((candidate) => candidate.id === lot.id)?.stage)
    const nextRow: BiomassDataRow = {
      id,
      date: harvestDraft.recordedAt,
      lotId: lot.id,
      lot: lot.lotKey || `Lot ${lot.id}`,
      stage: derivedStage,
      weight: '',
      dailyMortality: '',
      fishStock: '',
      upVol: String(harvestDraft.notes ?? ''),
      health: '',
      healthDetail: '',
      harvestKg,
      harvestCount
    }

    setHarvestSaving(true)
    setHarvestError(null)
    try {
      const productionResult = await upsertProductionUnitTableRowAndSync(productionContext, nextRow)

      // T3-D-03: mirror the Conteo write-parity pattern. Post-T3-D-04 the
      // ProductionRecord `harvestKg` / `harvestCount` cache columns will
      // be retired, so a Cosecha save that only touches the row cell
      // would vanish on the next `refreshProductionUnitTableFromApi`.
      // The authoritative value must live in a `HarvestLog` event
      // consumed by `overlayHarvestOnProductionRows` (SUM-PER-DAY). The
      // method here defaults to 'count' because the Cosecha draft does
      // not currently expose a method toggle — harvests are typically a
      // physical extraction with weight+count both directly measured.
      // Note the graceful numeric parsing: `harvestKg` may be empty
      // (count-only harvest) or comma-decimal; the API normalizer
      // handles both.
      const harvestCreated = await harvestLogApi.create({
        farmId: productionContext.farmId,
        productionUnitId: productionContext.productionUnitId,
        lot: lot.lotKey || `Lot ${lot.id}`,
        lotId: lot.id,
        productionRowId: id,
        recordedAt: harvestDraft.recordedAt,
        harvestKg: harvestKg || '0',
        harvestCount: harvestCount || undefined,
        method: 'count',
        notes: harvestDraft.notes ? String(harvestDraft.notes) : undefined,
        metadata: buildBiomassHarvestMetadata({
          productionRowId: id,
          lotId: lot.id,
          stage: derivedStage
        })
      })
      const nextHarvestLog = harvestCreated.data

      setProductionRows((prev) => [nextRow, ...prev])
      setHarvestLogs((prev) => [nextHarvestLog, ...prev])

      // PR-α3b U5: harvest-driven Lot.stage auto-transition. Evaluated only
      // after the HarvestLog write succeeds (an early throw above skips this
      // entirely, leaving any prior banner/pending-confirm state untouched).
      // tier-a-move-transfer-location-addendum.md follow-up: productionRows/
      // mortalityLogs/weightSampleLogs/fishStockLogs here are the same
      // farm/PU-scoped state crossFarmSupplement already exists to patch --
      // without it, a lot harvested right after a transfer would compare
      // against an artificially small currentBiomassKg (missing its
      // pre-transfer history), inflating harvestKg/currentBiomassKg and
      // risking a false "mark this lot fully Harvested" prompt.
      const harvestTransitionOutcome = resolveHarvestTransitionOutcome({
        lotId: lot.id,
        harvestLogs,
        newHarvestLog: nextHarvestLog,
        productionRows: [...productionRows, ...crossFarmSupplement.extraProductionRows],
        mortalityLogs: [...mortalityLogs, ...crossFarmSupplement.extraMortalityLogs],
        weightSampleLogs: [...weightSampleLogs, ...crossFarmSupplement.extraWeightSampleLogs],
        fishStockLogs: [...fishStockLogs, ...crossFarmSupplement.extraFishStockLogs],
        harvestDate: harvestDraft.recordedAt
      })

      if (harvestTransitionOutcome.signal === 'warning') {
        setHarvestTransitionWarning({ lotId: lot.id, ratio: harvestTransitionOutcome.ratio })
        setPendingHarvestTransitionLotId(null)
        setPendingHarvestTransitionPatch(null)
      } else if (harvestTransitionOutcome.signal === 'total') {
        setHarvestTransitionWarning(null)
        setPendingHarvestTransitionLotId(lot.id)
        setPendingHarvestTransitionPatch(harvestTransitionOutcome.lotUpdatePatch)
      } else {
        setHarvestTransitionWarning(null)
        setPendingHarvestTransitionLotId(null)
        setPendingHarvestTransitionPatch(null)
      }

      applyProductionTableStatus('saved', [nextRow.id], combineEntityMutationResults([
        productionResult,
        harvestCreated
      ]))
      resetHarvestDraft()
    } catch (error) {
      console.error('Harvest save failed', error)
      setHarvestError(t('biomass.error.saveFailed'))
    } finally {
      setHarvestSaving(false)
    }
  }

  // PR-α3b U5: second tap on the "Confirmar cierre del lote" button-flip CTA.
  // Two-step button-flip instead of `globalThis.confirm()` — silently
  // suppressed on iOS Chrome (finding #83) and this is the mobile
  // field-capture profile.
  const confirmHarvestTransition = async (lotId: string) => {
    if (pendingHarvestTransitionLotId !== lotId || !pendingHarvestTransitionPatch) return
    try {
      await lotApi.update(lotId, pendingHarvestTransitionPatch)
      setPendingHarvestTransitionLotId(null)
      setPendingHarvestTransitionPatch(null)
    } catch (error) {
      console.error('Harvest transition confirm failed', error)
      setHarvestError(t('biomass.error.saveFailed'))
    }
  }

  const cancelHarvestTransition = () => {
    setPendingHarvestTransitionLotId(null)
    setPendingHarvestTransitionPatch(null)
  }

  const getRowLinkedEventCount = useCallback((rowId: string): number => {
    const targetRow = productionRows.find((row) => row.id === rowId)
    if (!targetRow) return 0
    return countPlannedLinkedEventDeletes(planProductionRecordLinkedEventDeletes({
      targetRow,
      mortalityLogs,
      weightSampleLogs,
      fishStockLogs,
      harvestLogs
    }))
  }, [productionRows, mortalityLogs, weightSampleLogs, fishStockLogs, harvestLogs])

  const deleteLogRow = async (rowId: string, kind: BiomassRecentLogKind = 'sampling', reason = '') => {
    if (!canEditBiomassLogs) return
    if (!productionContext) return
    // Route the saving/error feedback to the section the row was deleted from,
    // so a failed Count/Harvest delete does not surface in the Sampling slot
    // (or freeze the Sampling save button) — each list has its own state.
    const setSaving = kind === 'count' ? setCountSaving : kind === 'harvest' ? setHarvestSaving : setLogSaving
    const setError = kind === 'count' ? setCountError : kind === 'harvest' ? setHarvestError : setLogError
    setSaving(true)
    setError(null)
    try {
      const targetRow = productionRows.find((row) => row.id === rowId) ?? { id: rowId, date: '', lot: '' }

      // I-2 delete-parity: every paired Tier A event on this row's
      // (calendar date, lot) — MortalityLog / WeightSampleLog / FishStockLog /
      // HarvestLog — is removed via the shared plan+execute helper so this
      // path and `Datatable.deleteRowById` cannot drift (see
      // src/screens/biomass/productionRecordCascadeDelete.ts and
      // docs/architecture-concerns.md I-2). Local state is reconciled from the
      // plan only after the deletes resolve.
      const linkedEventPlan = planProductionRecordLinkedEventDeletes({
        targetRow,
        mortalityLogs,
        weightSampleLogs,
        fishStockLogs,
        harvestLogs
      })
      const { mutationResults: linkedEventResults } = await executeProductionRecordLinkedEventDeletes(linkedEventPlan, {
        reason: t('productionRow.delete.auditReason', { reason: reason || t('productionRow.delete.noLinkedReason') })
      })

      if (linkedEventPlan.mortalityLogIds.length > 0) {
        const ids = new Set(linkedEventPlan.mortalityLogIds)
        setMortalityLogs((prev) => prev.filter((log) => !ids.has(log.id)))
      }
      if (linkedEventPlan.weightSampleLogIds.length > 0) {
        const ids = new Set(linkedEventPlan.weightSampleLogIds)
        setWeightSampleLogs((prev) => prev.filter((log) => !ids.has(log.id)))
      }
      if (linkedEventPlan.fishStockLogIds.length > 0) {
        const ids = new Set(linkedEventPlan.fishStockLogIds)
        setFishStockLogs((prev) => prev.filter((log) => !ids.has(log.id)))
      }
      if (linkedEventPlan.harvestLogIds.length > 0) {
        const ids = new Set(linkedEventPlan.harvestLogIds)
        setHarvestLogs((prev) => prev.filter((log) => !ids.has(log.id)))
      }

      const productionResult = await deleteProductionUnitTableRowAndSync(productionContext, rowId)
      setProductionRows((prev) => prev.filter((row) => row.id !== rowId))

      applyProductionTableStatus('deleted', [rowId], combineEntityMutationResults([
        ...linkedEventResults,
        productionResult
      ]))
    } catch (error) {
      console.error('Biomass log delete failed', error)
      setError(t('biomass.error.deleteFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="responsive-container">
      <BiomassContextSelector
        t={t}
        selectedFarmLabel={selectedFarmLabel}
        selectedProductionUnitLabel={selectedProductionUnitLabel}
        farmId={farmId}
        productionUnitId={productionUnitId}
        availableFarms={availableFarms}
        availableProductionUnits={availableProductionUnits}
        // Findings #212: lots shown without a weight sample (#209) count too.
        biomassKpisCount={biomassKPIs.length + activeLots.filter((kpi) => !(kpi.averageWeightG > 0)).length}
        onChangeFarm={(value) => {
          setFarmId(value)
          setProductionUnitId('')
        }}
        onChangeProductionUnit={(value) => setProductionUnitId(value)}
      />

      {importReviewNotice ? (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>Info</span>
          <div style={{ color: '#166534', flex: 1 }}>{importReviewNotice}</div>
        </div>
      ) : null}

      {farmId && productionUnitId && (
        <>
          {isSmallScreen ? (
            <div
              role="tablist"
              aria-label={t('biomass.aria.panels')}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 16,
                padding: 6,
                borderRadius: 10,
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff'
              }}
            >
              <button
                role="tab"
                aria-selected={activePanel === 'log'}
                type="button"
                onClick={() => setActivePanel('log')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: activePanel === 'log' ? '#3b82f6' : '#f3f4f6',
                  color: activePanel === 'log' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {t('biomass.panel.log')}
              </button>
              <button
                role="tab"
                aria-selected={activePanel === 'analyze'}
                type="button"
                onClick={() => setActivePanel('analyze')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: activePanel === 'analyze' ? '#3b82f6' : '#f3f4f6',
                  color: activePanel === 'analyze' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {t('biomass.panel.analyze')}
              </button>
            </div>
          ) : null}

          <div
            style={
              isSmallScreen
                ? undefined
                : {
                    display: 'grid',
                    gridTemplateColumns: 'minmax(340px, 520px) 1fr',
                    gap: 24,
                    alignItems: 'start'
                  }
            }
          >
            <BiomassLogPanel
              t={t}
              isVisible={!isSmallScreen || activePanel === 'log'}
              canEditBiomassLogs={canEditBiomassLogs}
              unitInactive={isUnitIdInactive(availableProductionUnits, productionUnitId)}
              statusMessage={productionTableStatusMessage}
              syncAttentionMessage={productionSyncAttentionMessage}
              syncLastResultSummary={productionSyncLastResultSummary}
              retryingSync={retryingProductionSync}
              refreshing={productionTableRefreshing}
              retrySync={handleRetryProductionSync}
              refreshLogs={refreshBiomassLogs}
              lotsLoading={lotsLoading}
              availableLots={availableLots}
              interactiveLotCount={interactiveLots.length}
              lotSelectPlaceholder={lotSelectPlaceholder}
              stockEmoji={stockEmoji}
              stockLabel={stockLabel}
              formatStageLabel={formatStageLabel}
              deletedLotLabels={deletedLotLabels}
              describeRowMortalityCauses={describeRowMortalityCauses}
              formatMethodLabel={formatMethodLabel}
              formatRowDateTime={formatRowDateTime}
              logDraft={logDraft}
              setLogDraft={setLogDraft}
              logSaving={logSaving}
              logError={logError}
              saveLogDraft={saveLogDraft}
              countDraft={countDraft}
              setCountDraft={setCountDraft}
              countSaving={countSaving}
              countError={countError}
              saveCountDraft={saveCountDraft}
              harvestDraft={harvestDraft}
              setHarvestDraft={setHarvestDraft}
              harvestSaving={harvestSaving}
              harvestError={harvestError}
              saveHarvestDraft={saveHarvestDraft}
              harvestTransitionWarning={harvestTransitionWarning}
              pendingHarvestTransitionLotId={pendingHarvestTransitionLotId}
              onConfirmHarvestTransition={confirmHarvestTransition}
              onCancelHarvestTransition={cancelHarvestTransition}
              recentSamplingRows={recentSamplingRows}
              recentCountRows={recentCountRows}
              countCorrection={{
                farmId: productionContext?.farmId,
                logIdByRowId: countLogIdByRowId,
                correctionCounts: countCorrectionCounts,
                onCorrected: async () => {
                  setCountCorrectionKey((key) => key + 1)
                  await refreshCurrentFishStockLogs({ forceRemote: true })
                  await refreshCurrentProductionRows({ forceRemote: true })
                }
              }}
              recentHarvestRows={recentHarvestRows}
              displayedEndOfDayStockByRowId={displayedEndOfDayStockByRowId}
              applyRowToDraft={applyRowToDraft}
              deleteLogRow={deleteLogRow}
              logListResetKey={productionContext ? `${productionContext.farmId}::${productionContext.productionUnitId}` : ''}
              getRowLinkedEventCount={getRowLinkedEventCount}
              onShowMortalityEvents={setMortalityEventsPopoverRowId}
              samplingEventCountByRowId={samplingEventCountByRowId}
              onShowSamplingEvents={setSamplingEventsPopoverRowId}
            />

            <BiomassAnalyzePanel
              t={t}
              isVisible={!isSmallScreen || activePanel === 'analyze'}
              viewMode={viewMode}
              setViewMode={setViewMode}
              analyzeBody={analyzeBody}
              stockLabel={stockLabel}
            />
          </div>
        </>
      )}
      {mortalityEventsPopoverRowId ? (() => {
        const row = productionRows.find((r) => String(r.id) === mortalityEventsPopoverRowId)
        if (!row) return null
        const events = findAllLinkedMortalityLogsForRow(row, mortalityLogs)
        const eventSum = events.reduce((sum, e) => sum + (Number.parseInt(e.mortalityCount, 10) || 0), 0)
        return (
          <MortalityEventsPopover
            events={events}
            lotLabel={String(row.lot ?? '')}
            dateLabel={String(row.date ?? '')}
            totalCount={String(eventSum)}
            farmId={productionContext?.farmId}
            onCorrectedEvent={async () => { await refreshCurrentMortalityLogs({ forceRemote: true }) }}
            onClose={() => setMortalityEventsPopoverRowId(null)}
            onDeleteEvent={async (eventId, reason) => {
              try {
                // findings #66: audited delete (reason from the Corregir… dialog).
                const result = await mortalityLogApi.delete(eventId, { reason })
                await result.syncPromise?.catch(() => undefined)
              } catch (error) {
                console.error('Biomass: mortality event delete failed', error)
              } finally {
                // Always drop from local state — if the API call failed because
                // the event no longer exists upstream (common for orphans
                // surfaced after finding #8) the local copy must still go.
                setMortalityLogs((prev) => prev.filter((log) => log.id !== eventId))
              }
            }}
          />
        )
      })() : null}
      {samplingEventsPopoverRowId ? (() => {
        const row = productionRows.find((r) => String(r.id) === samplingEventsPopoverRowId)
        if (!row) return null
        return (
          <SamplingEventsPopover
            productionRowId={samplingEventsPopoverRowId}
            farmId={productionContext?.farmId}
            productionUnitId={productionContext?.productionUnitId}
            lotLabel={String(row.lot ?? '')}
            dateLabel={String(row.date ?? '')}
            onCorrectedEvent={async () => { await refreshCurrentWeightSampleLogs({ forceRemote: true }) }}
            onClose={() => setSamplingEventsPopoverRowId(null)}
          />
        )
      })() : null}
    </div>
  )
}
